import {NgTemplateOutlet} from '@angular/common';
import {AfterViewChecked, Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircleInfo,
  faCopy,
  faCube,
  faEllipsisVertical,
  faEye,
  faEyeSlash,
  faFile,
  faFloppyDisk,
  faGear,
  faListCheck,
  faMemoCircleCheck,
  faPenToSquare,
  faPlay,
  faPlus,
  faTable,
  faTrash,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import {faChatbicAi} from '../../util/icons/custom-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicTextareaComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {
  CheckMode,
  collectMissingOptionMessages,
  DEFAULT_PRUEFEBENE,
  EINGABE_PARAMETER_TYPES,
  hasConfiguredEingabeparameter,
  HybridRule,
  isGeometryOnlyFileTypes,
  migrateOutputParameterOptions,
  needsOptions,
  normalizePruefebene,
  OptionsHolder,
  Pruefebene,
  PRUEFEBENE_DEFAULT_PARAMETERS,
  PRUEFEBENE_OPTIONS,
  Ruleset,
  SetEingabeparameter,
  SetOutputParameter,
  SetParameter,
} from '../models/rule-management.models';
import {StateConfigService} from '../../../services/state/state-config.service';
import {WorkflowService} from '../../../services/workflow.service';
import {MyCheckUsage, RulesetService} from '../../../services/ruleset.service';
import {RuleUsageWarningComponent} from '../rule-usage-warning/rule-usage-warning.component';
import {
  AI_INSTRUCTION_MODULE_ID,
  buildParameterOutputSchema,
  collectMissingSettings,
  deriveRuleResults,
  orderStepsByFlow,
  reconcileEingangParameters,
  RuleResultItem,
  ruleResultScope,
  SetParamDef,
} from '../../workflow-editor/workflow.models';
import {OptionsPopupComponent} from '../options-popup/options-popup.component';
import {ModuleManifest} from '../../../api/model/moduleManifest';
import {DocumentClassService} from '../../files/components/document-classes/document-class.service';
import {AiModelFieldComponent} from '../shared/ai-model-field/ai-model-field.component';
import {PermissionService} from '../../../services/permission/permission.service';
import {ChatService} from '../../chatBIC/service/chat.service';
import {MyCheckProposal, MyCheckProposalService} from '../../../services/mycheck-proposal.service';
import {DiscardOutcome} from '../../../guards/unsaved-changes.guard';
import {MyCheckFieldChange, ProposalChangeStatus} from '../proposal/mycheck-diff';
import {MapEntryChange, RuleEntryDecisions, RuleMapField, SetRemovedRuleEntry} from '../proposal/map-entry-decisions';
import {
  computeHybridDiff,
  extractHybridProposalDocument,
  HybridDiff,
  HybridProposalDocument,
  HybridProposalRule,
} from '../proposal/hybrid-mycheck-diff';
import {ChangeMarkerComponent} from '../../util/design/change-marker/change-marker.component';
import {ProposalRowComponent} from '../../util/design/proposal-row/proposal-row.component';
import {ProposalBannerComponent} from '../../util/design/proposal-banner/proposal-banner.component';
import {
  ProposalNavMarkerComponent,
  proposalNavTooltip,
} from '../../util/design/proposal-nav-marker/proposal-nav-marker.component';
import {
  ProposalReviewContext,
  ProposalReviewHost,
  ProposalReviewItem,
  ProposalReviewService,
  ProposalReviewSnapshot,
  revealProposalTarget,
} from '../../../services/chat/proposal-review.service';
import {contentKey, createMemo} from '../../../memo';
import {RuleTestService} from '../../../services/rule-test/rule-test.service';
import {
  RuleTestPopupContext,
  RuleTestQuota,
  RuleTestRuleOption,
  ruleTestContextKey,
} from '../../../services/rule-test/rule-test.models';
import {ruleTestLimitTooltip} from '../../../services/rule-test/rule-test.format';
import {RuleTestPopupComponent} from '../shared/rule-test-popup/rule-test-popup.component';
import {buildDefaultReportConfigForRuleset} from '../../my-checks/my-checks-builder/my-checks-builder.model';
import {RowDecisions} from '../proposal/row-decisions';

type HybridEditorSection = 'general' | 'parameters' | 'output-format' | 'rule';

/** Map-Feld einer Hybrid-Regel, über dessen Einträge einzeln entschieden wird – Hybrid kennt nur Tags. */
type HybridRuleMapField = Extract<RuleMapField, 'tagValues'>;

/** Einzelfelder einer Regel mit eigenem ✓/✗; `tagValues` wird je Schlüssel entschieden (siehe {@link RuleEntryDecisions}). */
type HybridRuleScalarPath = Exclude<keyof HybridProposalRule, 'id' | 'tagValues'>;

interface RuleOverviewInputItem {
  label: string;
  tags: string[];
}

interface RuleOverviewData {
  inputItems: RuleOverviewInputItem[];
  checkSteps: string[];
  resultItems: RuleResultItem[];
  resultScope: string;
}

@Component({
  selector: 'app-hybrid-editor',
  standalone: true,
  imports: [
    PageHeaderComponent,
    FaIconComponent,
    BicButtonComponent,
    BicBadgeComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    BicInputComponent,
    BicPopupComponent,
    BicSelectComponent,
    BicTextareaComponent,
    ReactiveFormsModule,
    OptionsPopupComponent,
    RuleUsageWarningComponent,
    AiModelFieldComponent,
    TooltipDirective,
    ChangeMarkerComponent,
    ProposalRowComponent,
    NgTemplateOutlet,
    ProposalBannerComponent,
    ProposalNavMarkerComponent,
    RuleTestPopupComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './hybrid-editor.component.html',
})
export class HybridEditorComponent implements OnInit, AfterViewChecked, OnDestroy, ProposalReviewHost {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();

  private static readonly MAX_RULE_COUNT = 30;
  private static readonly RULE_SHORT_CODE_MAX_LENGTH = 10;
  @ViewChild('allowedDocumentClassesSelectAllOption') allowedDocumentClassesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('allowedFileTypesSelectAllOption') allowedFileTypesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('selectedRuleAllowedDocumentClassesSelectAllOption') selectedRuleAllowedDocumentClassesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('selectedRuleAllowedFileTypesSelectAllOption') selectedRuleAllowedFileTypesSelectAllOption?: BicDropdownElementComponent;
  private suppressNextAllowedDocumentClassesChange = false;
  private suppressNextAllowedFileTypesChange = false;
  private suppressNextSelectedRuleAllowedDocumentClassesChange = false;
  private suppressNextSelectedRuleAllowedFileTypesChange = false;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  /** Set before this component's own updateDefinition (migration) so it doesn't trigger a self-reload. */
  private suppressNextDefinitionReload = false;
  /** True für die Dauer eines Speichervorgangs – unterdrückt die Reload-Kaskade (siehe save()). */
  private savingInProgress = false;
  selectedSection: HybridEditorSection = 'general';
  private stateConfigService = inject(StateConfigService);
  private flowService = inject(WorkflowService);
  private rulesetService = inject(RulesetService);
  private permissionService = inject(PermissionService);
  private toastService = inject(BicToastService);
  /** Set-Feld-Pfade je Sidebar-Abschnitt – für den Änderungs-Punkt in der Sektionsnavigation. Muss mit
   *  den `setConfig.…`-Pfaden übereinstimmen, die im Template per `app-change-marker` markiert sind. */
  private static readonly SECTION_SET_CONFIG_PATHS: Record<HybridEditorSection, string[]> = {
    general: ['name', 'description', 'setConfig.allowedFileTypes', 'setConfig.allowedDocumentClasses', 'setConfig.checkMode', 'setConfig.kiModel'],
    parameters: ['setConfig.pruefebene', 'setConfig.identifikatorParamId', 'setConfig.pruefebeneFilter',
      'setConfig.eingabeparameter', 'setConfig.tags'],
    'output-format': ['setConfig.outputParameters'],
    rule: [],
  };
  // ─── chatBIC-Vorschlag (Edit-Modus) ──────
  pendingProposal: MyCheckProposal | null = null;
  proposalDiff: HybridDiff | null = null;
  applyingProposalAction = false;
  protected chatService = inject(ChatService);
  /** Kopplung an die Review-Leiste im Edit-Chat (#2789): der Editor ist dort als Host angemeldet. */
  protected readonly proposalReview = inject(ProposalReviewService);
  protected readonly proposalNavTooltip = proposalNavTooltip;
  private unregisterReview?: () => void;
  /** Offene und einzeln übernommene Tag-Werte je Regel; der Pfad `tagValues` in
   *  `proposalDiff.ruleChanges.changed` bleibt die Sammelmarke, bis kein Eintrag mehr offen ist. */
  private readonly ruleEntryDecisions = new RuleEntryDecisions();
  /** Tag-IDs des Sets direkt nach dem Anwenden des Vorschlags (für {@link closeRuleEntriesOfMissingTags}). */
  private proposalTagIds = new Set<string>();
  protected readonly faChatbicAi = faChatbicAi;
  protected rulesetId = '';
  /** View-only Modus (bereitgestelltes Regelwerk ohne Bearbeitungsrecht). */
  protected readOnly = false;
  /** Das Set gehört einem anderen Projekt und ist nur hierher geteilt – bearbeitet wird es dort. */
  protected linkedFromOtherProject = false;
  private proposalService = inject(MyCheckProposalService);
  form: FormGroup = new FormBuilder().group({
    name: [''],
    description: [''],
    allowedDocumentClasses: [[]],
    allowedFileTypes: [[]],
    checkMode: ['SINGLE'],
  });

  ruleset!: Ruleset;
  rules: HybridRule[] = [];
  readonly MAX_RULES = HybridEditorComponent.MAX_RULE_COUNT;
  selectedRuleId: string | null = null;
  ruleShortCodes: Record<string, string> = {};
  availableDocumentClasses: { id: string; name: string }[] = [];
  inputParameters: { id: string; name: string; dataType: string; necessity: string }[] = [];
  checkParameters: { id: string; name: string; dataType: string; necessity: string }[] = [];
  // ─── Set-Level Regelparameter (Prüfebene, Identifikator, Eingabeparameter) ───
  pruefebene: Pruefebene = DEFAULT_PRUEFEBENE;
  identifikatorParamId = '';
  eingabeparameter: SetEingabeparameter[] = [];
  /** KI-Anweisung zur Filterung der Prüfebene (z. B. "nur Kapitel zum Brandschutz"). Nur bei pruefebene ≠ gesamtdokument relevant. */
  pruefebeneFilter = '';
  readonly pruefebeneOptions = PRUEFEBENE_OPTIONS;
  readonly eingabeParameterTypes = EINGABE_PARAMETER_TYPES;
  // ─── KI-Modell (Set-Ebene, analog KI-Prüfung) ────────────────────────────────
  // Ein Modell pro Regelset; wird beim Speichern in die eingangsparameter-Knoten der Regeln
  // synchronisiert und dort read-only als Badge angezeigt.
  /** Leer bis app-ai-model-field das Standardmodell des Kunden meldet bzw. das Set geladen ist. */
  aiModel = '';
  /** Pro Regel die IDs der genutzten (sichtbaren) Set-Eingabeparameter. Fehlt eine ID ⇒ versteckt. */
  ruleVisibleParamIds: Record<string, Set<string>> = {};
  tags: SetParameter[] = [];
  parameterDataTypes = ['Text', 'Zahl', 'Wahrheitswert', 'Leer', 'Auswahl'];
  outputParameters: SetOutputParameter[] = [];
  modusOptions = ['KI-Generiert', 'Eigener Wert'];
  dateitypOptions = ['Text', 'Zahl', 'Boolean', 'Auswahl'];
  private documentClassService = inject(DocumentClassService);
  availableFileTypes = ['IFC', 'PDF', 'Excel', 'Word', 'PowerPoint'];
  protected readonly faCopy = faCopy;

  // ─── Optionen-Popup (Tags, Ausgabe- und Eingabeparameter) ───────────────────
  optionsPopupVisible = false;
  optionsPopupLabel = '';
  optionsPopupOptions: string[] = [];
  /** Bestimmt, in welche Liste die Optionen zurückgeschrieben werden (siehe KI-Editor). */
  private optionsPopupKind: 'tag' | 'output' | 'input' | null = null;
  private optionsPopupTarget: OptionsHolder | null = null;

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faGear = faGear;
  protected readonly faPlus = faPlus;
  protected readonly faCube = faCube;
  protected readonly faPenToSquare = faPenToSquare;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faTrash = faTrash;
  protected readonly faCheck = faCheck;
  protected readonly faXmark = faXmark;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faTable = faTable;
  protected readonly faEye = faEye;
  protected readonly faEyeSlash = faEyeSlash;
  protected readonly faFloppyDisk = faFloppyDisk;
  protected readonly faPlay = faPlay;
  private initialState = '';

  // ─── Testlauf („Prüfregel testen", #2356) ───────────────────────────────────
  private ruleTestService = inject(RuleTestService);
  /** Beim Öffnen des Popups EINMAL berechnet (Feld, kein Getter – siehe Frontend/AGENTS.md). */
  ruleTestContext: RuleTestPopupContext | null = null;
  ruleTestQuota: RuleTestQuota | null = null;
  ruleTestUnseen = false;
  private ruleTestKey = '';
  private pendingRuleTestRunId: string | null = null;
  private ruleTestSubscriptions = new Subscription();
  private unregisterRuleTestEditor?: () => void;
  /**
   * Gespeichertes Manifest je Regel (Definition-ID), aus dem Laden des Sets. Der Testlauf aus dem
   * Set-Editor testet den Set-Stand des Editors mit dem GESPEICHERTEN Regel-Workflow (Plan E3).
   */
  private ruleManifestsById: Record<string, any> = {};
  /**
   * Modul-Registry für die Vorab-Prüfung der Regeln im Testlauf ({@link collectMissingSettings}).
   * Erst beim Öffnen des Testlaufs geladen – der Editor selbst braucht sie nicht.
   */
  private registryModules: ModuleManifest[] | null = null;

  /** True, solange ungespeicherte Änderungen vorliegen (steuert Speichern/Zurück-Button, siehe save()). */
  get isDirty(): boolean {
    return !this.readOnly && !!this.initialState && this.initialState !== this.serializeEditorState();
  }

  ruleToDelete: HybridRule | null = null;
  /** myChecks, die die zu löschende Prüfregel verwenden */
  deleteUsages: MyCheckUsage[] = [];
  deleteUsagesLoading = false;
  protected readonly faFile = faFile;
  private fb = new FormBuilder();

  private definition!: any;
  private manifest!: any;

  get selectedRule(): HybridRule | undefined {
    return this.rules.find(rule => rule.id === this.selectedRuleId);
  }

  get canCreateRule(): boolean {
    return this.rules.length < this.MAX_RULES;
  }

  get canAddRule(): boolean {
    return this.canCreateRule && !this.hasUneditedEmptyRule();
  }

  get maxRulesTooltip(): string {
    if (!this.canCreateRule) {
      return `Maximal ${this.MAX_RULES} Prüfregeln möglich`;
    }
    if (this.hasUneditedEmptyRule()) {
      return 'Bitte die neue leere Prüfregel zuerst bearbeiten.';
    }
    return 'Neu hinzufügen';
  }
  private ruleOverviewByRuleId: Record<string, RuleOverviewData> = {};

  get selectedRuleShortCode(): string {
    if (!this.selectedRuleId) return '';
    return this.normalizeRuleShortCode(this.ruleShortCodes[this.selectedRuleId]);
  }

  onAiModelChange(value: string): void {
    this.aiModel = value;
  }

  get sortedRules(): HybridRule[] {
    return [...this.rules].sort((a, b) => {
      const aKey = this.getRuleSortKey(a);
      const bKey = this.getRuleSortKey(b);
      const byPrimary = aKey.localeCompare(bKey, 'de', {sensitivity: 'base', numeric: true});
      if (byPrimary !== 0) return byPrimary;

      const byName = (a.name ?? '').localeCompare(b.name ?? '', 'de', {sensitivity: 'base', numeric: true});
      if (byName !== 0) return byName;

      return a.id.localeCompare(b.id, 'de', {sensitivity: 'base', numeric: true});
    });
  }

  get selectedRuleOverview(): RuleOverviewData {
    const empty: RuleOverviewData = {inputItems: [], checkSteps: [], resultItems: [], resultScope: ''};
    if (!this.selectedRuleId) {
      return empty;
    }
    return this.ruleOverviewByRuleId[this.selectedRuleId] ?? empty;
  }

  /** Per-rule possible results (persisted AI interpretation), not the ruleset-wide output params. */
  get overviewResultItems(): RuleResultItem[] {
    return this.selectedRuleOverview.resultItems;
  }

  /** Badge colour classes for a result tone (Erfüllt → green, Teilweise → yellow, Nicht erfüllt → red). */
  resultBadgeClasses(tone: string): string {
    switch (tone) {
      case 'success':
        return 'bg-green-50 text-green-500';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700';
      case 'error':
        return 'bg-red-50 text-red-600';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  getRuleListLabel(rule: HybridRule): string {
    return this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]) || rule.name || 'Prüfregel';
  }

  /** Wie {@link getRuleListLabel}, aber für eine vom Vorschlag entfernte Regel. */
  getRemovedRuleListLabel(rule: HybridProposalRule): string {
    return this.normalizeRuleShortCode(rule.shortCode ?? this.ruleShortCodes[rule.id]) || rule.name || 'Prüfregel';
  }

  selectSection(section: HybridEditorSection): void {
    this.selectedSection = section;
    if (section !== 'rule') {
      this.selectedRuleId = null;
    }
  }

  selectRule(rule: HybridRule): void {
    this.selectedSection = 'rule';
    this.selectedRuleId = rule.id;
  }
  /** Admin-Modus: globales Regelwerk ohne Projektkontext (Speichern an globale Endpunkte). */
  protected adminGlobal = false;
  private preProposalSnapshot: HybridProposalDocument | null = null;
  /**
   * Set-Feld-Pfade, die der Nutzer einzeln über den Übernehmen-Button eines `app-change-marker`
   * bestätigt hat. Ein globales „Verwerfen" im Banner darf diese Felder danach nicht mehr
   * stillschweigend zurücksetzen – siehe {@link buildPartialRevertDocument}.
   */
  private individuallyAcceptedPaths = new Set<string>();
  /** Wie {@link individuallyAcceptedPaths}, aber je Regel (`rule.id` → bestätigte Pfade dieser Regel). */
  private individuallyAcceptedRulePaths = new Map<string, Set<keyof HybridProposalRule>>();

  /** Regeln, die der aktuelle Vorschlag entfernen würde – wird nie angewendet (siehe Struktur-Guard),
   *  dient hier nur der Vollständigkeit für einen defensiven Hinweis in der Sidebar. */
  get removedProposalRules(): HybridProposalRule[] {
    return this.proposalDiff?.ruleChanges.removed ?? [];
  }

  /** (Re)load the ruleset + all child rule definitions and rebuild the overview snapshot. */
  /**
   * Selbstschutz gegen Deep-Links: `readOnly` kam bisher AUSSCHLIESSLICH aus dem Query-Parameter,
   * ein von Hand eingefügter oder gemerkter Link ohne `?readOnly=true` öffnete ein zentral
   * bereitgestelltes Regelwerk also voll editierbar. Bereitgestellte Sets sind nur zum Ansehen,
   * Ausführen und Duplizieren gedacht – die Bearbeitung läuft über den Admin-Bereich
   * (`adminGlobal`). Analog zu `MyChecksBuilderComponent`, das das für Prüfläufe schon tut.
   */
  private enforceReadOnlyForProvided(isProvided: boolean): void {
    if (this.adminGlobal || !isProvided) return;
    if (this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
    this.readOnly = true;
  }

  /**
   * Selbstschutz wie {@link enforceReadOnlyForProvided}, für ein aus einem anderen Projekt geteiltes
   * Set: die Übersichten öffnen es schreibgeschützt, ein Deep-Link oder „Öffnen" im Builder aber
   * voll editierbar. Bearbeitet wird es nur im Eigentümerprojekt – auch mit Regelwerk-Admin-Recht.
   */
  private enforceReadOnlyForLinked(ownerProjectId: number | null | undefined): void {
    if (this.adminGlobal || ownerProjectId == null) return;
    const currentProjectId = this.stateConfigService.getProjectIdOrUndefined();
    if (currentProjectId == null || Number(ownerProjectId) === Number(currentProjectId)) return;
    this.linkedFromOtherProject = true;
    this.readOnly = true;
  }

  ngOnInit(): void {
    // Vorgabemodell des Kunden; ein geladenes Regelset überschreibt es mit seinem `kiModel`.
    this.aiModel = this.stateConfigService.getDefaultAiModel();
    this.rulesetId = this.route.snapshot.paramMap.get('rulesetId') ?? '';
    this.readOnly = this.route.snapshot.queryParamMap.get('readOnly') === 'true';
    this.adminGlobal = this.route.snapshot.data['adminGlobal'] === true;
    if (!this.adminGlobal) {
      this.loadDocumentClasses();
    }
    this.initRuleTest();
    this.loadRuleset();
    this.unregisterReview = this.proposalReview.register(this);

    // Nach jedem Editor-Save (updateDefinition → definitionUpdated$) das myCheck neu laden,
    // damit die tabellarische Prüfregelübersicht den gespeicherten Stand zeigt statt des
    // Snapshots vom ersten Laden. Die eigene Migrations-Update wird per Flag ausgeklammert.
    this.flowService.definitionUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.savingInProgress) {
          return;
        }
        if (this.suppressNextDefinitionReload) {
          this.suppressNextDefinitionReload = false;
          return;
        }
        this.loadRuleset();
      });

    // Vorschlags-Signal aus dem Edit-Modus-Chat: `loadAndApplyProposal` prüft selbst noch einmal
    // readOnly/adminGlobal/rulesetId, das Abonnement selbst ist deshalb unbedingt.
    this.chatService.mycheckProposalReceived
      .pipe(filter(event => event.rulesetId === this.rulesetId), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.loadAndApplyProposal(event.proposalId));
  }

  ngAfterViewChecked(): void {
    this.allowedDocumentClassesSelectAllOption?.setSelected(this.areAllAllowedDocumentClassesSelected());

    const allFileTypesSelected = this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes);
    this.allowedFileTypesSelectAllOption?.setSelected(allFileTypesSelected);

    const selectedRuleAllowedDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    const allSelectedRuleDocumentClassesSelected = this.areAllValuesSelected(
      this.getSelectedRuleAllowedDocumentClassesSelectionValue(),
      selectedRuleAllowedDocumentClassIds,
    );
    this.selectedRuleAllowedDocumentClassesSelectAllOption?.setSelected(allSelectedRuleDocumentClassesSelected);

    const selectedRuleAllowedFileTypes = this.getRuleAllowedFileTypeOptions();
    const allSelectedRuleFileTypesSelected = this.areAllValuesSelected(
      this.getSelectedRuleAllowedFileTypesSelectionValue(),
      selectedRuleAllowedFileTypes,
    );
    this.selectedRuleAllowedFileTypesSelectAllOption?.setSelected(allSelectedRuleFileTypesSelected);
  }

  onSelectedRuleNameChange(value: string): void {
    if (!this.selectedRuleId) return;
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, name: value} : rule
    ));
  }

  onSelectedRuleDescriptionChange(value: string): void {
    if (!this.selectedRuleId) return;
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, description: value} : rule
    ));
  }

  onSelectedRuleShortCodeChange(value: string): void {
    if (!this.selectedRuleId) return;
    this.ruleShortCodes = {...this.ruleShortCodes, [this.selectedRuleId]: this.normalizeRuleShortCode(value)};
  }

  /** Öffnet den Chat im Edit-Modus für dieses (bereits gespeicherte) hybride myCheck. */
  openEditChat(): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    void this.chatService.openMycheckEditChat(this.rulesetId, 'HYBRID');
  }

  /**
   * Ohne dies blieb `activeSurface` beim Verlassen des Editors auf `'edit'` stehen (kein Gegenstück
   * zu `KiEditorComponent.ngOnDestroy`), sodass der schwebende Chat app-weit an diesem myCheck
   * hängen blieb, statt zum klassischen Chat zurückzukehren.
   */
  ngOnDestroy(): void {
    this.ruleTestSubscriptions.unsubscribe();
    this.unregisterRuleTestEditor?.();
    this.unregisterReview?.();
    // Beim Öffnen einer Regel dieses myChecks bleibt der Edit-Chat offen – dort schlägt er die
    // Regel-Logik vor (Regel-Editor, Propose-Hybrid-Rule-Change).
    if (!this.chatService.navigationStaysInHybridMycheck(this.router, this.rulesetId)) {
      this.chatService.closeMycheckSurface();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   TESTLAUF („Prüfregel testen", #2356)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Nur im eigenen Projekt und im bearbeitbaren Stand – nicht schreibgeschützt, nicht im Admin-Bereich. */
  get canRuleTest(): boolean {
    return !!this.ruleTestKey && !this.readOnly && !this.adminGlobal && !!this.ruleset;
  }

  get ruleTestLimitReached(): boolean {
    return this.ruleTestQuota?.remaining === 0;
  }

  /** Tooltip am deaktivierten Button (Figma 06); `undefined` blendet ihn aus – ein Leerstring zeigte einen leeren Pfeil. */
  get ruleTestTooltip(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.title ?? undefined as unknown as string;
  }

  get ruleTestTooltipSubtext(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.text ?? undefined as unknown as string;
  }

  private initRuleTest(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (this.adminGlobal || !projectId || !this.rulesetId) return;
    this.ruleTestKey = ruleTestContextKey(projectId, this.rulesetId);
    this.pendingRuleTestRunId = this.route.snapshot.queryParamMap.get('ruleTest');
    this.ruleTestSubscriptions.add(this.ruleTestService.quota$.subscribe(quota => this.ruleTestQuota = quota));
    this.ruleTestSubscriptions.add(
      this.ruleTestService.hasUnseenResult$(this.ruleTestKey).subscribe(unseen => this.ruleTestUnseen = unseen),
    );
    this.unregisterRuleTestEditor = this.ruleTestService.registerEditor(
      this.ruleTestKey, [(this.router.url ?? '').split('?')[0]], () => this.openRuleTest(),
    );
    this.ruleTestService.refreshQuota();
  }

  /** Nach dem ersten Laden: ein über `?ruleTest=` mitgegebener Lauf öffnet direkt das Popup. */
  private openPendingRuleTest(): void {
    const runId = this.pendingRuleTestRunId;
    this.pendingRuleTestRunId = null;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!runId || !projectId || !this.canRuleTest) return;
    this.ruleTestService.attach(projectId, this.ruleTestKey, runId);
    this.openRuleTest();
  }

  /**
   * Öffnet das Popup. Fehlt das Manifest einer Regel (z. B. gerade angelegt), wird es vorher geladen –
   * `buildRequest` muss synchron bleiben, die Regel ist im Popup frei wählbar.
   */
  openRuleTest(): void {
    if (!this.canRuleTest) return;
    const missing = this.rules.map(rule => rule.id).filter(id => !!id && !this.ruleManifestsById[id]);
    if (missing.length === 0 && this.registryModules) {
      this.ruleTestContext = this.buildRuleTestContext();
      return;
    }
    const manifests$ = missing.length
      ? forkJoin(missing.map(id => this.flowService.getDefinition(id).pipe(
        map(def => ({id, manifest: this.flowService.parseManifest(def) as any})),
        catchError(() => of({id, manifest: null as any})),
      )))
      : of([] as { id: string; manifest: any }[]);
    // Ohne Registry entfällt nur die Vorab-Prüfung der Einstellungen (das Modul meldet es dann
    // selbst) – der Testlauf darf daran nicht scheitern.
    const modules$ = this.registryModules
      ? of(this.registryModules)
      : this.flowService.listModules().pipe(catchError(() => of(null)));
    forkJoin([manifests$, modules$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([entries, modules]) => {
      for (const entry of entries) {
        if (entry.manifest) this.ruleManifestsById = {...this.ruleManifestsById, [entry.id]: entry.manifest};
      }
      if (modules) this.registryModules = modules;
      this.ruleTestContext = this.buildRuleTestContext();
    });
  }

  closeRuleTest(): void {
    this.ruleTestContext = null;
  }

  /**
   * Friert den Editor-Stand ein: Set-Konfiguration wie beim Speichern (`buildSetConfig`), dazu das
   * gespeicherte Manifest der gewählten Regel. Die Set-Werte mischt der Server wie beim Speichern in
   * die Regel ein.
   */
  buildRuleTestContext(): RuleTestPopupContext {
    const projectId = this.stateConfigService.getProjectIdOrUndefined() ?? 0;
    const f = this.form.value;
    const normalizedRules = this.rules.map(rule => ({
      ...rule,
      name: (rule.name ?? '').trim(),
      description: rule.description ?? '',
      allowedDocumentClasses: this.parseStoredDocumentClasses(rule.allowedDocumentClasses),
      allowedFileTypes: this.parseStoredRuleAllowedFileTypeSelection(rule.allowedFileTypes),
    }));
    const setConfig = structuredClone(this.buildSetConfig(f, normalizedRules));
    const manifests = structuredClone(this.ruleManifestsById);
    const ruleFileTypes = (setConfig['ruleAllowedFileTypes'] ?? {}) as Record<string, string[]>;
    const ruleDocumentClasses = (setConfig['ruleAllowedDocumentClasses'] ?? {}) as Record<string, string[]>;
    const rules: RuleTestRuleOption[] = normalizedRules.map(rule => ({
      key: rule.id,
      shortCode: this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]),
      name: rule.name,
      allowedFileTypes: ruleFileTypes[rule.id] ?? [],
      allowedDocumentClasses: ruleDocumentClasses[rule.id] ?? [],
    }));
    const selected = this.selectedRuleId;
    const editorDirty = this.isDirty;
    const checkMode = (f.checkMode ?? 'SINGLE') as CheckMode;
    return {
      projectId,
      projectName: this.stateConfigService.getProject()?.name ?? '',
      rulesetType: 'HYBRID',
      rulesetId: this.rulesetId,
      setLabel: (f.name ?? '') || this.ruleset?.name || '',
      rules,
      preselectedRuleKey: rules.some(rule => rule.key === selected) ? selected : (rules[0]?.key ?? null),
      ruleLocked: false,
      setAllowedFileTypes: (setConfig['allowedFileTypes'] as string[] | undefined) ?? [],
      setAllowedDocumentClasses: (setConfig['allowedDocumentClasses'] as string[] | undefined) ?? [],
      editorDirty,
      modelLabel: this.aiModel || null,
      validationErrors: this.collectRuleTestErrors(),
      // Je Regel: unvollständige Einstellungen ihrer Schritte (z.B. Relationsprüfung ohne IFC-Klasse)
      // – blockiert nur, wenn genau diese Regel im Popup gewählt ist.
      ruleValidationErrors: Object.fromEntries(rules.map(rule => [
        rule.key,
        manifests[rule.key] && this.registryModules
          ? collectMissingSettings(manifests[rule.key], this.registryModules)
          : [],
      ])),
      contextKey: this.ruleTestKey,
      buildRequest: (ruleKey, files) => ({
        rulesetType: 'HYBRID',
        rulesetId: this.rulesetId,
        ruleKey,
        editorDirty,
        files,
        hybrid: {setConfig, ruleManifest: manifests[ruleKey] ?? null},
      }),
      reportConfig: buildDefaultReportConfigForRuleset(
        this.rulesetId, 'HYBRID', this.pruefebene, this.outputParameters, checkMode,
      ),
    };
  }

  /** Dieselben Sperren wie beim Speichern (`save()`), dazu: ohne Regel gibt es nichts zu testen. */
  private collectRuleTestErrors(): string[] {
    const errors: string[] = [];
    if (this.rules.length === 0) errors.push('Das Prüfregelset enthält noch keine Prüfregel.');
    errors.push(...this.getMissingOptionMessages());
    if (this.eingabeparameterRequired && !hasConfiguredEingabeparameter(this.eingabeparameter)) {
      errors.push('Eingabeparameter: Mindestens ein benannter Parameter ist erforderlich.');
    }
    return errors;
  }

  /**
   * Bei reinen IFC-Regelsets entfällt die Parameter-Pflicht: der Eingangsparameter reicht dort nur
   * die Datei weiter und extrahiert nichts (siehe {@link isGeometryOnlyFileTypes}). Vorher musste
   * man einen wirkungslosen Platzhalter-Parameter anlegen, um überhaupt speichern zu können.
   */
  get eingabeparameterRequired(): boolean {
    return !isGeometryOnlyFileTypes(this.getAllowedFileTypesValue());
  }

  /**
   * Regelset speichern (Set-Definition + je Prüfregel eine Kind-Definition).
   *
   * @param onDone Was nach dem letzten erfolgreichen Write passiert. Default markiert nur als
   *   gespeichert (der Speichern-Button wird dadurch zum Zurück-Button — ein zweiter Klick
   *   navigiert dann tatsächlich weg). {@link openRuleEditor} übergibt stattdessen die Navigation
   *   in den Workflow-Editor: dieser Sprung verließ den Regelset-Editor früher ungespeichert, und
   *   da es hier keinen CanDeactivate-Guard gibt, waren die Set-Änderungen still weg.
   *   Im Fehlerfall greift {@link onSaveFailed} und `onDone` läuft NICHT — sonst wäre der
   *   Datenverlust nur verschoben.
   */
  save(onDone: () => void = () => this.toastService.success('myCheck gespeichert')): boolean {
    if (this.readOnly) return false;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!this.adminGlobal && !projectId) return false;

    // Auswahl ohne Optionen fällt zur Laufzeit still auf freien Text zurück – nicht speichern.
    const missingOptions = this.getMissingOptionMessages();
    if (missingOptions.length > 0) {
      this.toastService.warning(missingOptions[0]);
      return false;
    }
    if (this.eingabeparameterRequired && !hasConfiguredEingabeparameter(this.eingabeparameter)) {
      this.toastService.warning('Eingabeparameter: Mindestens ein benannter Parameter ist erforderlich.');
      return false;
    }

    // Speichern mit offenem Vorschlag übernimmt ihn: das Formular enthält ihn bereits vollständig.
    // Als übernommen gilt er erst, wenn alle Writes durch sind – scheitert die Validierung oder das
    // Speichern ({@link onSaveFailed}), bleibt er offen, und der nächste Speichern-Klick schließt ihn ab.
    const proposal = this.pendingProposal;

    // Sperre der Reload-Kaskade lösen, bevor der Aufrufer weitermacht (navigateBack tut das
    // ebenfalls; doppelt ist harmlos, weil idempotent).
    const done = () => {
      this.savingInProgress = false;
      this.markSaved();
      this.markPendingProposalApplied(proposal);
      onDone();
    };

    // Der Speichervorgang schreibt 1 + N Definitionen (Set + je Regel). Jeder dieser Writes
    // emittiert definitionUpdated$, was ohne diese Sperre je ein komplettes loadRuleset() samt
    // eigenem GET pro Regel auslöste — quadratisch viele Requests pro Speichern. Die Sperre gilt
    // für den ganzen Vorgang; danach wird ohnehin navigiert bzw. einmal neu geladen.
    this.savingInProgress = true;

    const f = this.form.value;
    const normalizedRules = this.rules.map(rule => ({
      ...rule,
      name: (rule.name ?? '').trim(),
      description: rule.description ?? '',
      allowedDocumentClasses: this.parseStoredDocumentClasses(rule.allowedDocumentClasses),
      allowedFileTypes: this.parseStoredRuleAllowedFileTypeSelection(rule.allowedFileTypes),
    }));

    this.rules = normalizedRules;

    const ruleById = new Map(normalizedRules.map(rule => [rule.id, rule]));
    this.manifest.steps = (this.manifest.steps ?? []).map((step: any) => {
      const rule = ruleById.get(step.nodeId);
      if (!rule) return step;
      return {
        ...step,
        inputs: {
          ...(step.inputs ?? {}),
          '__name': rule.name || step.inputs?.['__name'] || 'Prüfregel',
          '__description': rule.description,
          '__shortCode': this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]),
        },
      };
    });

    const setConfig = this.buildSetConfig(f, normalizedRules);

    this.manifest.name = f.name ?? this.ruleset.name;
    this.manifest.description = f.description ?? this.ruleset.description;
    this.manifest.trigger = this.manifest.trigger ?? {nodeId: 'trigger_0', moduleId: 'MANUAL_TRIGGER', config: {}};
    this.manifest.trigger.config = {
      ...(this.manifest.trigger.config ?? {}),
      hybridEditor: setConfig,
    };
    // Backward compatibility for already-stored manifests that read from manifest.config
    this.manifest.config = setConfig;

    this.persistDefinition(this.ruleset.id, this.manifest).subscribe({
      next: () => {
        const childLoadRequests = normalizedRules.map(rule =>
          this.loadDefinition(rule.id).pipe(
            map(def => ({rule, def})),
            catchError(() => of(null)),
          ),
        );

        if (childLoadRequests.length === 0) {
          done();
          return;
        }

        forkJoin(childLoadRequests).subscribe({
          next: loadedChildren => {
            const childUpdateRequests = loadedChildren
              .map(entry => {
                if (entry === null) {
                  return null;
                }
                const childManifest = this.flowService.parseManifest(entry.def);
                childManifest.name = entry.rule.name || childManifest.name || 'Prüfregel';
                childManifest.description = entry.rule.description ?? '';
                // Set-Definition (Name/Typ/Beschreibung/Prüfebene/Identifikator/KI-Modell und die
                // regelbezogenen Dokumentenklassen) in den eingangsparameter-Knoten synchronisieren;
                // dort sind diese Werte read-only Badges. Der per-Regel hidden-Zustand wird dabei
                // ERHALTEN (Sichtbarkeit wird im Workflow-Editor gesteuert, nicht hier).
                const existingParams = Array.isArray(childManifest.trigger?.config?.parameter)
                  ? childManifest.trigger.config.parameter
                  : [];
                const ruleParams = reconcileEingangParameters(existingParams, this.setParamDefs(), this.identifikatorParamId);
                childManifest.trigger = childManifest.trigger ?? {
                  nodeId: 'trigger_0',
                  moduleId: 'eingangsparameter',
                  config: {},
                };
                childManifest.trigger.config = {
                  ...(childManifest.trigger.config ?? {}),
                  parameter: ruleParams,
                  pruefebene: this.pruefebene,
                  modell: this.aiModel,
                  erlaubte_dokumentenklassen: this.ruleDocumentClassesForNode(entry.rule),
                  identifikatorParamId: this.identifikatorParamId,
                  outputSchema: buildParameterOutputSchema({parameter: ruleParams}),
                };
                this.applySetAiModelToSteps(childManifest);
                return this.persistDefinition(entry.rule.id, childManifest).pipe(
                  catchError(() => of(null)),
                );
              })
              .filter((request): request is NonNullable<typeof request> => request !== null);

            if (childUpdateRequests.length === 0) {
              done();
              return;
            }

            forkJoin(childUpdateRequests).subscribe({
              next: () => done(),
              error: err => this.onSaveFailed('Fehler beim Speichern der Prüfregeln', err),
            });
          },
          error: err => this.onSaveFailed('Fehler beim Laden der Prüfregeln', err),
        });
      },
      error: err => this.onSaveFailed('Fehler beim Speichern des Regelsets', err),
    });
    return true;
  }

  /**
   * Baut das Vorschlagsdokument, das `save()` tatsächlich schreiben würde – als Grundlage für den
   * Chat-Vorschlags-Diff (`computeHybridDiff`). Nutzt dieselbe
   * `buildSetConfig()` wie `save()`, damit beide Pfade nicht auseinanderlaufen.
   */
  buildHybridDocumentSnapshot(): HybridProposalDocument {
    const f = this.form.value;
    const normalizedRules = this.rules.map(rule => ({
      ...rule,
      name: (rule.name ?? '').trim(),
      description: rule.description ?? '',
      allowedDocumentClasses: this.parseStoredDocumentClasses(rule.allowedDocumentClasses),
      allowedFileTypes: this.parseStoredRuleAllowedFileTypeSelection(rule.allowedFileTypes),
    }));
    const setConfig = this.buildSetConfig(f, normalizedRules);
    return {
      name: f.name ?? this.ruleset.name,
      description: f.description ?? this.ruleset.description,
      setConfig,
      rules: normalizedRules.map(rule => ({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        shortCode: this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]),
        allowedDocumentClasses: (setConfig['ruleAllowedDocumentClasses'] as Record<string, string[]>)[rule.id] ?? [],
        allowedFileTypes: (setConfig['ruleAllowedFileTypes'] as Record<string, string[]>)[rule.id] ?? [],
        tagValues: rule.tagValues ?? {},
      })),
    };
  }
  /**
   * Parameterlisten, über deren Zeilen einzeln entschieden wird (Schlüssel = Feldname in `setConfig`). Die
   * Sammelmarke `setConfig.<Schlüssel>` in {@link proposalDiff} bleibt bestehen, bis jede Zeile der Liste
   * entschieden ist.
   */
  private readonly rowLists: Record<string, RowDecisions<object>> = {
    eingabeparameter: this.createRowList('eingabeparameter', () => this.eingabeparameter, rows => this.eingabeparameter = rows as typeof this.eingabeparameter),
    tags: this.createRowList('tags', () => this.tags, rows => this.tags = rows as typeof this.tags),
    // Prüfparameter-Zeilen leitet der Editor selbst ab (siehe buildSetConfig) – sie gehören nicht zum Vorschlag.
    outputParameters: this.createRowList('outputParameters', () => this.outputParameters, rows => this.outputParameters = rows as typeof this.outputParameters,
      () => this.outputParameters.filter(param => param.modus !== 'Prüfparameter')),
  };

  /**
   * Banner „Änderungen übernehmen": markiert den Vorschlag serverseitig als APPLIED (Bookkeeping) UND
   * persistiert die Formularwerte über denselben Pfad wie der Speichern-Button (`save()`).
   */
  onProposalApplyClicked(): void {
    // Proposals entstehen nur über loadAndApplyProposal()/loadPendingProposalIfAny(), beide brechen
    // bei adminGlobal bereits ab (siehe dort) – hier also unbedingt ein Projekt vorhanden.
    if (!this.pendingProposal || !this.stateConfigService.getProjectIdOrUndefined() || this.applyingProposalAction) return;
    this.save();
  }

  /**
   * Wird generisch vom `UnsavedChangesGuard` aufgerufen, wenn der Nutzer beim Wegnavigieren
   * „Verwerfen" wählt. Ohne den Vorschlags-Fall würde hier nur `initialState` auf den aktuellen
   * (bereits mit dem Vorschlag befüllten) Formularstand neu gesetzt – der Vorschlag also
   * stillschweigend als „gespeichert" behandelt, statt wirklich verworfen zu werden.
   */
  discardChanges(): DiscardOutcome {
    if (this.pendingProposal) {
      if (this.hasIndividuallyAcceptedChanges()) {
        // „Verwerfen" im Dialog: eigene, während des Vorschlags gemachte Änderungen gehen mit, nur die
        // übernommenen bleiben. Scheitert deren Speichern an der Validierung, bleibt der Nutzer hier.
        return this.keepAcceptedChangesAndLeave('snapshot') ? 'savedAccepted' : 'blocked';
      }
      this.onProposalDiscardClicked();
      return 'discarded';
    }
    this.initialState = this.serializeEditorState();
    return 'discarded';
  }

  /** True, solange der Vorschlag noch mindestens eine offene (nicht einzeln entschiedene) Änderung
   *  enthält. Steuert den Banner „Änderungen übernehmen". */
  get hasOpenProposalChanges(): boolean {
    const diff = this.proposalDiff;
    if (!diff) return false;
    return diff.fieldChanges.size > 0
      || diff.ruleChanges.added.length > 0
      || diff.ruleChanges.removed.length > 0
      || diff.ruleChanges.changed.size > 0;
  }

  /**
   * Übernehmen-Button eines einzelnen `app-change-marker` (Set-Feld, z. B. `setConfig.kiModel`): der
   * Feldwert steht bereits korrekt im Formular (das hat `applyProposal` schon erledigt), hier wird nur
   * die Markierung entfernt und der Pfad vor einem späteren globalen „Verwerfen" geschützt.
   */
  onFieldAccepted(path: string): void {
    this.individuallyAcceptedPaths.add(path);
    this.proposalDiff?.fieldChanges.delete(path);
    this.settleProposalIfResolved();
  }

  /** Verwerfen-Button eines einzelnen `app-change-marker`: Feldwert auf den Vorher-Stand zurücksetzen. */
  onFieldRejected(path: string): void {
    this.individuallyAcceptedPaths.delete(path);
    this.proposalDiff?.fieldChanges.delete(path);
    this.revertFieldToSnapshot(path);
    this.settleProposalIfResolved();
  }

  /** Wie {@link onFieldAccepted}, aber für ein Regelfeld (z. B. `description`). Tag-Werte entscheidet
   *  {@link onRuleEntryAccepted} je Schlüssel. */
  onRuleFieldAccepted(rule: HybridRule, path: HybridRuleScalarPath): void {
    const acceptedPaths = this.individuallyAcceptedRulePaths.get(rule.id) ?? new Set<keyof HybridProposalRule>();
    acceptedPaths.add(path);
    this.individuallyAcceptedRulePaths.set(rule.id, acceptedPaths);
    this.removeRuleChangedPath(rule.id, path);
    this.settleProposalIfResolved();
  }

  private removeRuleChangedPath(ruleId: string, path: string): void {
    const changedPaths = this.proposalDiff?.ruleChanges.changed.get(ruleId);
    if (!changedPaths) return;
    const remaining = changedPaths.filter(p => p !== path);
    if (remaining.length > 0) {
      this.proposalDiff!.ruleChanges.changed.set(ruleId, remaining);
    } else {
      this.proposalDiff!.ruleChanges.changed.delete(ruleId);
    }
  }

  private readDocumentField(doc: HybridProposalDocument, path: string): unknown {
    if (path === 'name' || path === 'description') return doc[path];
    const key = path.startsWith('setConfig.') ? path.slice('setConfig.'.length) : path;
    return doc.setConfig[key];
  }

  /** Liefert `doc` mit genau einem auf `value` gesetzten Feld – der Rest bleibt unverändert. */
  private withDocumentField(doc: HybridProposalDocument, path: string, value: unknown): HybridProposalDocument {
    if (path === 'name' || path === 'description') return {...doc, [path]: value};
    const key = path.startsWith('setConfig.') ? path.slice('setConfig.'.length) : path;
    return {...doc, setConfig: {...doc.setConfig, [key]: value}};
  }

  /**
   * Setzt EIN Set-Feld auf seinen Vorher-Wert zurück, ohne die übrigen (bereits angewandten oder schon
   * einzeln entschiedenen) Felder anzutasten: aktuellen Stand snapshotten, nur den einen Pfad patchen,
   * das gepatchte Dokument komplett neu anwenden (dieselbe Schreib-Logik wie beim ersten Anwenden).
   */
  private revertFieldToSnapshot(path: string): void {
    if (!this.preProposalSnapshot) return;
    const current = this.buildHybridDocumentSnapshot();
    const patched = this.withDocumentField(current, path, this.readDocumentField(this.preProposalSnapshot, path));
    this.applyHybridDocumentToEditor(patched);
  }

  /** Wie {@link revertFieldToSnapshot}, aber für ein einzelnes Feld einer einzelnen Regel. Tag-Werte
   *  setzt {@link revertRuleEntryToSnapshot} je Schlüssel zurück. */
  private revertRuleFieldToSnapshot(ruleId: string, path: HybridRuleScalarPath): void {
    if (!this.preProposalSnapshot) return;
    const before = this.preProposalSnapshot.rules.find(r => r.id === ruleId);
    if (!before) return;
    const current = this.buildHybridDocumentSnapshot();
    const patchedRules = current.rules.map(r => r.id === ruleId ? {...r, [path]: before[path]} : r);
    this.applyHybridDocumentToEditor({...current, rules: patchedRules});
  }

  /** Setzt genau einen Eintrag einer Regel-Map auf den Vorher-Wert zurück (fehlte er vorher, wird er
   *  gelöscht); alle übrigen Einträge und Felder bleiben, wie sie sind. */
  private revertRuleEntryToSnapshot(ruleId: string, field: HybridRuleMapField, key: string): void {
    if (!this.preProposalSnapshot) return;
    const before = this.snapshotRuleMap(ruleId, field);
    const current = this.buildHybridDocumentSnapshot();
    const patchedRules = current.rules.map(r => r.id === ruleId
      ? {...r, [field]: this.withMapEntriesFrom(r[field], before, [key])}
      : r);
    this.applyHybridDocumentToEditor({...current, rules: patchedRules});
  }

  /** Kopie von `target`, in der die `keys` den Wert aus `source` tragen; leere Werte in `source` gelten
   *  als „nicht vorhanden“ und löschen den Schlüssel (wie `diffMapEntries` in `map-entry-decisions.ts`). */
  private withMapEntriesFrom(target: Record<string, string> | undefined, source: Record<string, string> | undefined,
                             keys: string[]): Record<string, string> {
    const result = {...(target ?? {})};
    for (const key of keys) {
      const value = source?.[key];
      if (value) {
        result[key] = value;
      } else {
        delete result[key];
      }
    }
    return result;
  }

  private snapshotRuleMap(ruleId: string, field: HybridRuleMapField): Record<string, string> | undefined {
    return this.preProposalSnapshot?.rules.find(r => r.id === ruleId)?.[field];
  }

  /** Public (wie `KiEditorComponent.applyProposal`) – erlaubt gezieltes Testen ohne den vollen
   *  Chat-Signal-Umweg über `loadAndApplyProposal`. */
  applyProposal(proposal: MyCheckProposal): void {
    // Ein noch offener Vorschlag (vom Backend inzwischen SUPERSEDED) steckt bereits im Formular. Erst
    // zurücksetzen, sonst wäre er der „Vorher"-Stand des neuen und ein Verwerfen fiele nicht auf den
    // gespeicherten Stand zurück. Einzeln übernommene Felder bleiben dabei erhalten.
    if (this.pendingProposal) {
      this.revertPendingProposal();
    }
    this.individuallyAcceptedPaths.clear();
    this.individuallyAcceptedRulePaths.clear();
    this.ruleEntryDecisions.reset();
    this.resetRowLists();
    this.preProposalSnapshot = this.buildHybridDocumentSnapshot();
    const doc = extractHybridProposalDocument(proposal.document?.manifest);
    const diff = computeHybridDiff(this.preProposalSnapshot, doc);
    if (this.hasUnsupportedStructureChange(diff)) {
      this.preProposalSnapshot = null;
      this.toastService.error('Der Vorschlag legt Regeln an oder löscht sie – das unterstützt der Hybrid-Editor nicht. Bitte im Chat korrigieren.');
      return;
    }
    this.proposalDiff = diff;
    this.applyHybridDocumentToEditor(doc);
    this.initRowLists();
    this.initRuleEntryDecisions();
    this.proposalTagIds = new Set(this.tags.map(tag => tag.id));
    this.pendingProposal = proposal;
    // Kein markSaved() – isDirty bleibt true, der bestehende Speichern-Button fährt den normalen save().
    // Bleibt nach dem Anwenden nichts zu entscheiden (z. B. nur `{a: ''}` gegen `{}`), wird der Vorschlag
    // sofort abgeschlossen – sonst bliebe er ohne Banner und Leiste offen und käme nach einem Reload wieder.
    this.settleProposalIfResolved();
  }

  /** Banner „Verwerfen" (und `discardChanges()` bei pending Vorschlag): Formular zurücksetzen + Backend informieren. */
  onProposalDiscardClicked(): void {
    const proposal = this.pendingProposal;
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    this.revertPendingProposal();
    if (projectId) {
      this.proposalService.discard(this.rulesetId, proposal.id, projectId).subscribe({
        error: err => console.error('Verwerfen im Backend fehlgeschlagen (Formular bereits zurückgesetzt):', err),
      });
    }
  }

  /** Vorher/Nachher einer Zeile für `app-change-marker`, `undefined` wenn unverändert. */
  rowChange(key: string, row: object, index: number): MyCheckFieldChange | undefined {
    return this.rowLists[key].change(row, index);
  }

  /** Wie {@link onFieldRejected}, aber für ein Regelfeld. */
  onRuleFieldRejected(rule: HybridRule, path: HybridRuleScalarPath): void {
    this.individuallyAcceptedRulePaths.get(rule.id)?.delete(path);
    this.removeRuleChangedPath(rule.id, path);
    this.revertRuleFieldToSnapshot(rule.id, path);
    this.settleProposalIfResolved();
  }

  /**
   * Sind alle Änderungen des Vorschlags einzeln entschieden, wird er abgeschlossen – ohne den Banner gibt
   * es keinen „Übernehmen"-Klick mehr, der das übernähme. Mit übernommenen Änderungen werden diese
   * gespeichert und der Vorschlag als APPLIED markiert, sonst gilt er als verworfen. Ohne das bliebe er im
   * Backend offen und käme nach einem Reload erneut.
   */
  private settleProposalIfResolved(): void {
    if (!this.pendingProposal || this.hasOpenProposalChanges) return;
    if (this.hasIndividuallyAcceptedChanges()) {
      // Alles ist entschieden: es gibt nichts mehr zurückzunehmen, eigene Änderungen werden mitgespeichert.
      this.keepAcceptedChangesAndLeave('current');
    } else {
      this.onProposalDiscardClicked();
    }
  }

  // ─── Listen: Entscheidung je Zeile ───────────────────────────────────────────

  /** Vorher/Nachher eines einzelnen Felds einer Zeile, `undefined` wenn das Feld unverändert ist. */
  rowFieldChange(key: string, row: object, index: number, field: string): MyCheckFieldChange | undefined {
    return this.rowLists[key].fieldChange(row, index, field);
  }

  /** Wartet diese Zeile noch auf ✓/✗? */
  rowPending(key: string, row: object): boolean {
    return this.rowLists[key].isPending(row);
  }

  /** Rechter Einzug der Spaltenüberschriften, solange die Liste ✓/✗-Spalten zeigt. */
  rowGutter(key: string): number {
    return this.rowLists[key].pending ? ProposalRowComponent.GUTTER_PX : 0;
  }

  /** Zeilen, die der Vorschlag aus der Liste entfernt (stehen nicht mehr im Formular). */
  removedRows(key: string): { before: { name?: string }; index: number }[] {
    return this.rowLists[key].removed as { before: { name?: string }; index: number }[];
  }

  acceptRow(key: string, row: object): void {
    this.rowLists[key].accept(row);
  }

  rejectRow(key: string, row: object): void {
    this.rowLists[key].reject(row);
  }

  acceptRowRemoval(key: string, entry: { before: object; index: number }): void {
    this.rowLists[key].acceptRemoval(entry);
  }

  rejectRowRemoval(key: string, entry: { before: object; index: number }): void {
    this.rowLists[key].rejectRemoval(entry);
  }

  /**
   * Baut das Dokument für ein globales „Verwerfen" im Banner (bzw. für das Abschließen nach der letzten
   * Einzelentscheidung): der AKTUELLE Stand, in dem nur die noch offenen Änderungen des Vorschlags auf den
   * Vorher-Wert zurückgesetzt werden. Eigene Änderungen, die der Nutzer während des offenen Vorschlags
   * gemacht hat, bleiben so stehen – vom Snapshot aus gingen sie stillschweigend verloren. Einzeln
   * übernommene oder verworfene Änderungen stehen nicht mehr im Diff und bleiben, wie sie sind. Regeln
   * anlegen/löschen kann ein Hybrid-Vorschlag nicht (siehe {@link applyProposal}).
   */
  private buildRevertOpenChangesDocument(snapshot: HybridProposalDocument, diff: HybridDiff): HybridProposalDocument {
    const current = this.buildHybridDocumentSnapshot();
    let result = current;
    for (const path of diff.fieldChanges.keys()) {
      const rowKey = Object.keys(this.rowLists).find(key => this.rowListPath(key) === path);
      const value = rowKey && this.rowLists[rowKey].diff
        ? this.rowLists[rowKey].revertPending(current.setConfig[rowKey] as object[])
        : this.readDocumentField(snapshot, path);
      result = this.withDocumentField(result, path, value);
    }
    return {
      ...result,
      rules: result.rules.map(rule => {
        const openPaths = diff.ruleChanges.changed.get(rule.id) as (keyof HybridProposalRule)[] | undefined;
        const before = openPaths?.length ? snapshot.rules.find(r => r.id === rule.id) : undefined;
        if (!openPaths || !before) return rule;
        let patched = rule;
        for (const path of openPaths) {
          // Tags: nur die noch offenen Einträge zurücksetzen – einzeln übernommene behalten ihren Wert.
          patched = path === 'tagValues'
            ? {...patched, tagValues: this.withMapEntriesFrom(patched.tagValues, before.tagValues,
                this.ruleEntryDecisions.openKeys(rule.id, 'tagValues'))}
            : {...patched, [path]: before[path]};
        }
        return patched;
      }),
    };
  }

  /**
   * Baut das Dokument für „Verwerfen" im Verlassen-Dialog: grundsätzlich der Vorher-Snapshot, AUSSER für
   * Pfade, die der Nutzer zuvor einzeln über einen `app-change-marker` bestätigt hat – die behalten
   * ihren aktuellen (übernommenen) Wert, statt von einem globalen Verwerfen mit zurückgesetzt zu
   * werden (siehe Klassenkommentar {@link individuallyAcceptedPaths}). Anders als
   * {@link buildRevertOpenChangesDocument} fallen hier auch eigene Änderungen weg: der Nutzer hat
   * „Verwerfen" gewählt, gespeichert wird nur, was er am Vorschlag ausdrücklich übernommen hat.
   */
  private buildPartialRevertDocument(snapshot: HybridProposalDocument): HybridProposalDocument {
    const current = this.buildHybridDocumentSnapshot();
    let result = snapshot;
    for (const path of this.individuallyAcceptedPaths) {
      result = this.withDocumentField(result, path, this.readDocumentField(current, path));
    }
    for (const [key, list] of Object.entries(this.rowLists)) {
      // Eine fertig entschiedene Liste hat keinen Diff mehr; mit übernommenen Zeilen gilt ihr aktueller Stand.
      if (!list.diff && !list.accepted) continue;
      result = this.withDocumentField(result, this.rowListPath(key),
        list.revertPending(current.setConfig[key] as object[]));
    }
    result = {
      ...result,
      rules: result.rules.map(rule => {
        const acceptedPaths = this.individuallyAcceptedRulePaths.get(rule.id) ?? new Set<keyof HybridProposalRule>();
        const acceptedTagKeys = this.ruleEntryDecisions.acceptedKeys(rule.id, 'tagValues');
        if (!acceptedPaths.size && !acceptedTagKeys.length) return rule;
        const currentRule = current.rules.find(r => r.id === rule.id);
        if (!currentRule) return rule;
        let patched = rule;
        for (const path of acceptedPaths) {
          patched = {...patched, [path]: currentRule[path]};
        }
        // Tags: Vorher-Stand plus die einzeln übernommenen Einträge aus dem aktuellen Stand.
        if (acceptedTagKeys.length) {
          patched = {...patched, tagValues: this.withMapEntriesFrom(patched.tagValues, currentRule.tagValues, acceptedTagKeys)};
        }
        return patched;
      }),
    };
    return result;
  }

  /**
   * Schließt einen Vorschlag als übernommen ab (Backend-Buchführung plus lokaler Zustand). Wird von
   * {@link save} nach erfolgreichem Speichern aufgerufen – egal ob über den Banner oder den Speichern-Button.
   * Den lokalen Zustand räumt es nur ab, wenn inzwischen kein neuerer Vorschlag angewendet wurde.
   */
  private markPendingProposalApplied(proposal: MyCheckProposal | null): void {
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (this.pendingProposal === proposal) {
      this.pendingProposal = null;
      this.proposalDiff = null;
      this.preProposalSnapshot = null;
      this.individuallyAcceptedPaths.clear();
      this.individuallyAcceptedRulePaths.clear();
      this.ruleEntryDecisions.reset();
      this.resetRowLists();
    }
    if (!projectId) return;
    this.applyingProposalAction = true;
    this.proposalService.apply(this.rulesetId, proposal.id, projectId).subscribe({
      error: err => {
        console.error('Vorschlag konnte nicht als APPLIED markiert werden (wird trotzdem gespeichert):', err);
        this.applyingProposalAction = false;
      },
      complete: () => this.applyingProposalAction = false,
    });
  }

  private createRowList(key: string, rows: () => object[], setRows: (rows: object[]) => void,
                        comparableRows?: () => object[]): RowDecisions<object> {
    return new RowDecisions<object>({rows, setRows, comparableRows, decided: () => this.onRowListDecided(key)});
  }

  private rowListPath(key: string): string {
    return `setConfig.${key}`;
  }

  private resetRowLists(): void {
    Object.values(this.rowLists).forEach(list => list.reset());
  }

  private captureRowLists(): Record<string, object[]> {
    return Object.fromEntries(Object.entries(this.rowLists).map(([key, list]) => [key, list.captureRows()]));
  }

  private rebindRowLists(previousRows: Record<string, object[]>): void {
    for (const [key, list] of Object.entries(this.rowLists)) list.rebind(previousRows[key] ?? []);
  }

  private anyRowAccepted(): boolean {
    return Object.values(this.rowLists).some(list => list.accepted);
  }

  /** Berechnet nach dem Anwenden eines Vorschlags je Liste, welche Zeilen er neu anlegt, ändert oder entfernt. */
  private initRowLists(): void {
    const snapshot = this.preProposalSnapshot;
    this.resetRowLists();
    if (!snapshot) return;
    for (const [key, list] of Object.entries(this.rowLists)) {
      if (!this.proposalDiff?.fieldChanges.has(this.rowListPath(key))) continue;
      list.init((snapshot.setConfig[key] ?? []) as object[]);
      this.syncRowList(key);
    }
  }

  /** Entfernt die Sammelmarke, sobald keine Zeile der Liste mehr offen ist. */
  private syncRowList(key: string): void {
    const list = this.rowLists[key];
    if (list.diff && !list.pending) {
      this.proposalDiff?.fieldChanges.delete(this.rowListPath(key));
      list.diff = null;
    }
  }

  private onRowListDecided(key: string): void {
    this.syncRowList(key);
    if (key === 'tags') {
      this.closeRuleEntriesOfMissingTags();
    }
    this.settleProposalIfResolved();
  }

  /**
   * Kopplung Set ↔ Regel: Hat der Nutzer einen vom Vorschlag neu angelegten Tag auf Set-Ebene verworfen,
   * sind auch die Regelwerte, die der Vorschlag dafür setzt, verworfen – wie bei „Alle verwerfen“. Sie
   * werden auf den Stand vor dem Vorschlag zurückgesetzt (den Schlüssel gab es dort nicht, er fällt weg) und
   * geschlossen; ohne das blieben verwaiste `tagValues` stehen und würden beim nächsten Speichern
   * geschrieben. Andere offene Einträge ohne Tag im Set bleiben offen und stehen schreibgeschützt nach den
   * Tags ({@link unlistedRuleEntries}).
   */
  private closeRuleEntriesOfMissingTags(): void {
    const tagIds = new Set(this.tags.map(tag => tag.id));
    const snapshotTagIds = new Set(((this.preProposalSnapshot?.setConfig['tags'] ?? []) as SetParameter[]).map(tag => tag.id));
    const rejectedTagIds = [...this.proposalTagIds].filter(id => !tagIds.has(id) && !snapshotTagIds.has(id));
    if (rejectedTagIds.length === 0) return;
    for (const rule of this.rules) {
      for (const key of this.ruleEntryDecisions.openKeys(rule.id, 'tagValues')) {
        if (!rejectedTagIds.includes(key)) continue;
        this.revertRuleEntryToSnapshot(rule.id, 'tagValues', key);
        if (this.ruleEntryDecisions.decide(rule.id, 'tagValues', key, 'rejected')) {
          this.removeRuleChangedPath(rule.id, 'tagValues');
        }
      }
    }
  }

  private hasIndividuallyAcceptedChanges(): boolean {
    return this.individuallyAcceptedPaths.size > 0
      || this.anyRowAccepted()
      || [...this.individuallyAcceptedRulePaths.values()].some(paths => paths.size > 0)
      || this.ruleEntryDecisions.hasAccepted();
  }

  /**
   * Verlassen mit einzeln übernommenen Feldern: nur die noch offenen Änderungen werden verworfen, die
   * bestätigten Felder werden gespeichert. Ohne das gingen sie beim Verlassen verloren, obwohl der
   * Nutzer sie ausdrücklich übernommen hat. Der Vorschlag gilt dann als APPLIED (teilweise übernommen).
   *
   * Als APPLIED markiert erst {@link save} nach erfolgreichem Speichern: scheitert die Validierung oder ein
   * Write, bleibt der Vorschlag offen (ohne Markierungen), und der nächste Speichern-Klick schließt ihn ab –
   * sonst wäre er serverseitig erledigt, obwohl nichts gespeichert wurde.
   *
   * @param base `'current'` nimmt nur die noch offenen Änderungen zurück (eigene bleiben, siehe
   *   {@link buildRevertOpenChangesDocument}); `'snapshot'` (Verlassen-Dialog) verwirft auch eigene.
   * @returns ob gespeichert wird – `false`, wenn die Validierung das Speichern verhindert.
   */
  private keepAcceptedChangesAndLeave(base: 'current' | 'snapshot'): boolean {
    const proposal = this.pendingProposal;
    if (!proposal) return false;
    this.revertPendingProposal(base);
    this.pendingProposal = proposal;
    // Läuft über die Komponente hinaus weiter; das Speichern braucht keinen Bildschirm.
    return this.save();
  }

  /** Regel-Sidebar: durch den aktuellen Vorschlag geändert (mind. ein Feld weicht ab). */
  isProposalRuleChanged(rule: HybridRule): boolean {
    return !!this.proposalDiff?.ruleChanges.changed.has(rule.id);
  }

  /**
   * Vorher/Nachher für ein einzelnes Regelfeld (z. B. `tagValues`), für `app-change-marker` in der
   * Regel-Detailansicht. `proposalDiff.ruleChanges.changed` kennt nur die geänderten *Pfade* je Regel,
   * nicht die Werte – die werden hier aus dem Snapshot (Vorher) und der bereits angewandten Regel
   * (Nachher) nachgeschlagen.
   */
  ruleFieldChange(rule: HybridRule, path: keyof HybridProposalRule): MyCheckFieldChange | undefined {
    if (!this.proposalDiff?.ruleChanges.changed.get(rule.id)?.includes(path)) return undefined;
    const before = this.preProposalSnapshot?.rules.find(r => r.id === rule.id);
    if (!before) return undefined;
    const beforeValue = before[path];
    const afterValue = (rule as unknown as Record<string, unknown>)[path];
    // Gecacht, weil das Objekt an [change] haengt (siehe memo.ts). Inhalt als
    // Schluessel, weil die Werte Arrays sein koennen (allowedDocumentClasses,
    // tagValues), die der Editor in place veraendern darf - eine Referenz bliebe
    // dabei gleich und die Aenderungsanzeige veraltet.
    return this.memo(`fieldChange:${rule.id}:${String(path)}`,
      [contentKey(beforeValue), contentKey(afterValue)],
      (): MyCheckFieldChange => ({before: beforeValue, after: afterValue}));
  }

  /** Zeigt einen Änderungs-Punkt in der Sektionsnavigation, wenn eine der Sektion zugehörigen
   *  Set-Feld-Pfade im aktuellen Vorschlag abweicht. */
  sectionHasProposalChange(section: HybridEditorSection): boolean {
    const paths = HybridEditorComponent.SECTION_SET_CONFIG_PATHS[section];
    if (!paths?.length || !this.proposalDiff) return false;
    return paths.some(path => this.proposalDiff!.fieldChanges.has(path));
  }

  /** Marker in der Sektionsnavigation (#2789): grüner Punkt, solange der Abschnitt offene Änderungen hat. */
  sectionNavStatus(section: HybridEditorSection): ProposalChangeStatus | null {
    return this.sectionHasProposalChange(section) ? 'changed' : null;
  }

  /** Marker in der Regelnavigation. Neue Regeln gibt es im Hybrid-Editor nicht (Struktur-Guard in
   *  {@link applyProposal}), eine Regel ist also nur geändert oder nicht. */
  ruleNavStatus(rule: HybridRule): ProposalChangeStatus | null {
    return this.isProposalRuleChanged(rule) ? 'changed' : null;
  }

  // ─── Tags je Schlüssel (#2789) ──────────────────────────────────────────────

  /**
   * Vorher/Nachher eines offenen Tag-Werts der Regel für `app-change-marker`, `undefined` wenn der
   * Eintrag nicht (mehr) offen ist. Vorher/Nachher kommen bei jedem Aufruf aus Snapshot und aktueller
   * Regel, der Status bleibt der beim Anwenden festgelegte (siehe `RuleEntryDecisions.initialStatus`):
   * Leert der Nutzer einen geänderten Wert, bleibt das Feld editierbar („Geändert“), statt als „Wird
   * gelöscht“ unter die Tags zu springen; stellt er ihn von Hand auf den Vorher-Wert, bleibt er entscheidbar.
   */
  ruleEntryChange(rule: HybridRule, field: HybridRuleMapField, key: string): MapEntryChange | undefined {
    const status = this.ruleEntryDecisions.initialStatus(rule.id, field, key);
    if (!status) return undefined;
    const beforeValue = this.snapshotRuleMap(rule.id, field)?.[key];
    const afterValue = rule[field]?.[key];
    // Gecacht, weil das Objekt an [change] haengt (siehe memo.ts); die Werte sind Strings.
    return this.memo(`entryChange:${rule.id}:${field}:${key}`, [beforeValue, afterValue, status],
      (): MapEntryChange => ({before: beforeValue, after: afterValue, status}));
  }

  /**
   * Ob ein Tag-Wert in der normalen Tag-Reihe des Regel-Details steht: Der Tag ist in
   * {@link ruleParameterTags} und der Eintrag wurde beim Anwenden nicht als „Wird gelöscht“ markiert.
   */
  isRuleEntryListed(rule: HybridRule, field: HybridRuleMapField, key: string): boolean {
    const status = this.ruleEntryDecisions.initialStatus(rule.id, field, key);
    if (status === undefined) return true;
    if (status === 'removed') return false;
    return this.ruleParameterTags.some(tag => tag.id === key);
  }

  /**
   * Offene Einträge, die nicht in der normalen Tag-Reihe stehen: vom Vorschlag zu löschende (`value` = alter
   * Wert) und neue bzw. geänderte Werte zu einem Schlüssel ohne (benannten) Tag im Set (`value` = aktueller
   * Wert). Sie stehen danach schreibgeschützt mit ✓/✗ – ohne sie zählte die Review-Leiste Einträge ohne
   * Entscheidung. Reihenfolge wie im Stepper ({@link orderedOpenTagKeys}).
   */
  unlistedRuleEntries(rule: HybridRule, field: HybridRuleMapField): { key: string; name: string; value: string; change: MapEntryChange }[] {
    const entries = this.orderedOpenTagKeys(rule)
      .filter(key => !this.isRuleEntryListed(rule, field, key))
      .map(key => ({key, change: this.ruleEntryChange(rule, field, key)}))
      .filter((entry): entry is { key: string; change: MapEntryChange } => !!entry.change)
      .map(({key, change}) => ({
        key, change, name: this.ruleEntryName(key),
        value: String((change.status === 'removed' ? change.before : change.after) ?? ''),
      }));
    // Gecacht, weil die Liste im Template iteriert und je Eintrag an [change] gebunden wird.
    return this.memo(`unlistedEntries:${rule.id}:${field}`,
      entries.flatMap(entry => [entry.key, entry.change, entry.name, entry.value]), () => entries);
  }

  /**
   * Regelparameter (Tags), die der Vorschlag auf Set-Ebene löscht, deren Wert in dieser Regel er aber nicht
   * selbst entfernt – ohne diese Liste fehlte der Tag in der Tag-Reihe des Regel-Details ganz. Er erscheint
   * schreibgeschützt als „Wird gelöscht“ mit dem aktuellen (alten) Wert. ✓/✗ sind an die Set-Zeile gekoppelt
   * ({@link acceptRowRemoval}/{@link rejectRowRemoval}): Ein Tag kann nur für alle Regeln zugleich gelöscht
   * oder behalten werden, so widersprechen sich Regel-Detail und Set-Ebene nie (wie im KI-Editor,
   * `KiEditorComponent.setRemovedRuleEntries`). Einträge mit eigener Regel-Entscheidung stehen in
   * {@link unlistedRuleEntries}.
   */
  setRemovedRuleEntries(rule: HybridRule, field: HybridRuleMapField): SetRemovedRuleEntry[] {
    const setIds = new Set(this.tags.map(tag => tag.id));
    const removals = this.removedRows('tags') as SetRemovedRuleEntry['removal'][];
    const entries = removals.flatMap((removal): SetRemovedRuleEntry[] => {
      const key = removal.before.id;
      if (!key || setIds.has(key)) return [];
      if (this.ruleEntryDecisions.initialStatus(rule.id, field, key) !== undefined) return [];
      const value = String(rule[field]?.[key] ?? '');
      const change = this.memo(`setRemovedChange:${rule.id}:${field}:${key}`, [value],
        (): MapEntryChange => ({before: value, after: undefined, status: 'removed'}));
      return [{key, name: removal.before.name?.trim() || key, value, change, removal}];
    });
    // Gecacht, weil die Liste im Template iteriert und je Eintrag an [change] gebunden wird.
    return this.memo(`setRemovedEntries:${rule.id}:${field}`,
      entries.flatMap(entry => [entry.key, entry.name, entry.value, entry.change, entry.removal]), () => entries);
  }

  /** Offene Tag-Einträge einer Regel in Anzeigereihenfolge: wie die Tag-Reihe, dann Einträge ohne Tag im
   *  Set, zu löschende zuletzt. */
  private orderedOpenTagKeys(rule: HybridRule): string[] {
    const tagIds = this.ruleParameterTags.map(tag => tag.id);
    const rank = (key: string): number => {
      if (this.ruleEntryDecisions.initialStatus(rule.id, 'tagValues', key) === 'removed') return tagIds.length + 1;
      const index = tagIds.indexOf(key);
      return index >= 0 ? index : tagIds.length;
    };
    return this.ruleEntryDecisions.openKeys(rule.id, 'tagValues').sort((a, b) => rank(a) - rank(b));
  }

  /** Name eines Tags: aus der aktuellen Set-Liste, sonst aus dem Stand vor dem Vorschlag, sonst die ID. */
  private ruleEntryName(key: string): string {
    const snapshotTags = (this.preProposalSnapshot?.setConfig['tags'] ?? []) as SetParameter[];
    return this.tags.find(tag => tag.id === key)?.name?.trim()
      || snapshotTags.find(tag => tag.id === key)?.name?.trim()
      || key;
  }

  /** ✓ an einem Tag-Wert: der Wert steht schon in der Regel, nur der Eintrag wird geschlossen und vor
   *  einem späteren globalen „Verwerfen“ geschützt. */
  onRuleEntryAccepted(rule: HybridRule, field: HybridRuleMapField, key: string): void {
    if (this.ruleEntryDecisions.decide(rule.id, field, key, 'accepted')) {
      this.removeRuleChangedPath(rule.id, field);
    }
    this.settleProposalIfResolved();
  }

  /** ✗ an einem Tag-Wert: nur diesen Schlüssel auf den Vorher-Wert zurücksetzen. */
  onRuleEntryRejected(rule: HybridRule, field: HybridRuleMapField, key: string): void {
    this.revertRuleEntryToSnapshot(rule.id, field, key);
    if (this.ruleEntryDecisions.decide(rule.id, field, key, 'rejected')) {
      this.removeRuleChangedPath(rule.id, field);
    }
    this.settleProposalIfResolved();
  }

  /** Legt nach dem Anwenden eines Vorschlags die offenen Tag-Einträge je geänderter Regel fest. Bleibt
   *  keiner übrig (z. B. `{a: ''}` gegen `{}`), entfällt die Sammelmarke sofort. */
  private initRuleEntryDecisions(): void {
    const snapshot = this.preProposalSnapshot;
    const diff = this.proposalDiff;
    if (!snapshot || !diff) return;
    for (const [ruleId, paths] of [...diff.ruleChanges.changed]) {
      if (!paths.includes('tagValues')) continue;
      const after = this.rules.find(rule => rule.id === ruleId)?.tagValues;
      if (this.ruleEntryDecisions.init(ruleId, 'tagValues', this.snapshotRuleMap(ruleId, 'tagValues'), after) === 0) {
        this.removeRuleChangedPath(ruleId, 'tagValues');
      }
    }
  }

  // ─── Review-Leiste im Edit-Chat (ProposalReviewHost, #2789) ─────────────────

  get reviewRulesetId(): string {
    return this.rulesetId;
  }

  /**
   * Offene ✓/✗-Entscheidungen in der Reihenfolge der Navigation: Abschnitte (Set-Felder, Listen je
   * Zeile), übrige Set-Felder, dann Regeln in Sidebar-Reihenfolge (Einzelfelder, Tags je Schlüssel).
   * Leer genau dann, wenn {@link hasOpenProposalChanges} false ist – jeder offene Diff-Eintrag liefert
   * mindestens ein Item.
   */
  proposalReviewSnapshot(): ProposalReviewSnapshot | null {
    const proposal = this.pendingProposal;
    const diff = this.proposalDiff;
    if (!proposal || !diff || !this.hasOpenProposalChanges) return null;
    const items: ProposalReviewItem[] = [];

    const sectionPaths = new Set<string>();
    for (const section of ['general', 'parameters', 'output-format'] as const) {
      for (const path of HybridEditorComponent.SECTION_SET_CONFIG_PATHS[section]) {
        sectionPaths.add(path);
        if (diff.fieldChanges.has(path)) items.push(...this.fieldReviewItems(path, () => this.selectSection(section)));
      }
    }
    for (const path of diff.fieldChanges.keys()) {
      if (!sectionPaths.has(path)) items.push(...this.fieldReviewItems(path, () => this.selectSection('general')));
    }

    const ruleOrder = new Map(this.sortedRules.map((rule, index) => [rule.id, index]));
    const rank = (ruleId: string) => ruleOrder.get(ruleId) ?? ruleOrder.size;
    const changedRuleIds = [...diff.ruleChanges.changed.keys()].sort((a, b) => rank(a) - rank(b));
    for (const ruleId of changedRuleIds) {
      const rule = this.rules.find(r => r.id === ruleId);
      const select = () => {
        if (rule) this.selectRule(rule);
      };
      const paths = [...(diff.ruleChanges.changed.get(ruleId) ?? [])]
        .sort((a, b) => this.ruleFieldOrder(a) - this.ruleFieldOrder(b));
      for (const path of paths) {
        const tagKeys = path === 'tagValues' && rule ? this.orderedOpenTagKeys(rule) : [];
        if (tagKeys.length) {
          tagKeys.forEach(key => items.push(this.reviewItem(`rule:${ruleId}:tagValues:${key}`, select)));
        } else {
          items.push(this.reviewItem(`rule:${ruleId}:${path}`, select));
        }
      }
    }
    // Neue/entfernte Regeln lässt der Struktur-Guard in applyProposal nie zu; gezählt werden sie trotzdem,
    // damit die Leiste nie weniger zeigt, als hasOpenProposalChanges meldet.
    diff.ruleChanges.added.forEach(rule => items.push(this.reviewItem(`rule-added:${rule.id}`)));
    diff.ruleChanges.removed.forEach(rule => items.push(this.reviewItem(`rule-removed:${rule.id}`)));

    return {
      proposalId: proposal.id,
      items,
      stale: !!proposal.stale,
      busy: this.applyingProposalAction || this.savingInProgress,
    };
  }

  proposalReviewContext(): ProposalReviewContext | null {
    if (!this.ruleset) return null;
    const rule = this.selectedSection === 'rule' ? this.selectedRule : undefined;
    return {mycheckName: this.ruleset.name, itemLabel: rule ? this.getRuleListLabel(rule) : undefined};
  }

  acceptAllProposalChanges(): void {
    this.onProposalApplyClicked();
  }

  discardAllProposalChanges(): void {
    this.onProposalDiscardClicked();
  }

  /** Ein Set-Feld = ein Item; eine Zeilenliste ein Item je offener Zeile (Template: `row:<liste>:<index>`
   *  bzw. `row:<liste>:removed:<index>`). */
  private fieldReviewItems(path: string, select: () => void): ProposalReviewItem[] {
    const rowKey = Object.keys(this.rowLists).find(key => this.rowListPath(key) === path);
    const list = rowKey ? this.rowLists[rowKey] : undefined;
    if (!rowKey || !list?.diff) return [this.reviewItem(`field:${path}`, select)];
    const rows: Record<string, object[]> = {
      eingabeparameter: this.eingabeparameter,
      tags: this.tags,
      outputParameters: this.outputParameters,
    };
    const items = (rows[rowKey] ?? [])
      .map((row, index) => list.isPending(row) ? this.reviewItem(`row:${rowKey}:${index}`, select) : null)
      .filter((item): item is ProposalReviewItem => item !== null);
    list.removed.forEach((_, index) => items.push(this.reviewItem(`row:${rowKey}:removed:${index}`, select)));
    return items.length ? items : [this.reviewItem(`field:${path}`, select)];
  }

  /** Reihenfolge der Regelfelder wie im Regel-Detail. */
  private ruleFieldOrder(path: string): number {
    const index = HybridEditorComponent.RULE_FIELD_ORDER.indexOf(path);
    return index >= 0 ? index : HybridEditorComponent.RULE_FIELD_ORDER.length;
  }

  private static readonly RULE_FIELD_ORDER: readonly string[] = [
    'shortCode', 'name', 'description', 'tagValues', 'allowedFileTypes', 'allowedDocumentClasses',
  ];

  private reviewItem(key: string, select?: () => void): ProposalReviewItem {
    return {
      key,
      reveal: () => {
        select?.();
        revealProposalTarget(key);
      },
    };
  }

  private loadRuleset(): void {
    const rulesetId = this.rulesetId;

    // Load ruleset definition from backend
    this.flowService.getDefinition(rulesetId).subscribe(def => {
      this.definition = def;
      this.manifest = this.flowService.parseManifest(def);
      const setConfig = this.getSetConfig(
        this.manifest?.trigger?.config?.hybridEditor ?? this.manifest?.config
      );
      this.ruleShortCodes = this.normalizeRuleShortCodeMap(setConfig.ruleShortCodes);
      this.ruleset = {
        id: def.id ?? rulesetId,
        name: def.name ?? this.manifest.name ?? 'Hybrider myCheck',
        description: this.manifest.description ?? '',
        type: 'HYBRID',
        ruleCount: this.manifest.steps?.length ?? 0,
        allowedFileTypes: this.parseStoredAllowedFileTypes(setConfig.allowedFileTypes),
        checkMode: setConfig.checkMode ?? 'SINGLE',
        isPrebuilt: def.projectId == null,
      };
      this.enforceReadOnlyForProvided(def.projectId == null || !!(def as any).readOnly);
      this.enforceReadOnlyForLinked(def.projectId);
      this.initForm();
      this.restoreSetConfig(setConfig);

      const steps = this.manifest.steps ?? [];

      let needsMigration = false;
      for (const step of steps) {
        if (!step.workflowDefinitionId && step.nodeId) {
          step.workflowDefinitionId = step.nodeId;
          needsMigration = true;
        }

      }
      if (needsMigration) {
        const pid = this.stateConfigService.selectedProject?.id;
        if (pid) {
          // Eigene Migrations-Persistierung: den daraus folgenden definitionUpdated$ nicht als Reload deuten.
          this.suppressNextDefinitionReload = true;
          this.flowService.updateDefinition(this.ruleset.id, this.manifest, pid).subscribe();
        }
      }

      if (steps.length === 0) {
        this.rules = [];
        this.ruleOverviewByRuleId = {};
        this.ruleManifestsById = {};
        this.markSaved();
        this.loadPendingProposalIfAny();
        this.openPendingRuleTest();
        return;
      }

      // Load each child definition to get the current name (may have been renamed)
      const childRequests = steps.map((step: any) =>
        this.flowService.getDefinition(step.nodeId).pipe(
          map(childDef => {
            const childManifest = this.flowService.parseManifest(childDef);
            return {
              id: childDef.id ?? step.nodeId,
              name: childDef.name ?? step.inputs?.['__name'] ?? 'Prüfregel',
              description: childDef.description ?? step.inputs?.['__description'] ?? '',
              moduleCount: this.flowService.getModuleCountFromDef(childDef),
              overview: this.buildRuleOverviewFromManifest(childManifest),
              nodeParams: this.extractNodeParams(childManifest),
              manifest: childManifest as any,
            };
          }),
          catchError(() => of({
            id: step.nodeId ?? '',
            name: step.inputs?.['__name'] ?? step.moduleId ?? 'Prüfregel',
            description: step.inputs?.['__description'] ?? '',
            moduleCount: 1,
            overview: {inputItems: [], checkSteps: [], resultItems: [], resultScope: ''},
            nodeParams: [] as any[],
            manifest: null as any,
          })),
        ),
      );

      forkJoin(childRequests).subscribe(rules => {
        const loadedRules = (rules ?? []) as Array<HybridRule & { overview?: RuleOverviewData; nodeParams?: any[]; manifest?: any }>;
        this.ruleManifestsById = Object.fromEntries(
          loadedRules.filter(rule => !!rule.manifest).map(rule => [rule.id, rule.manifest]),
        );
        const allowedDocumentClassesByRuleId = this.parseStoredRuleAllowedDocumentClasses(setConfig.ruleAllowedDocumentClasses);
        const allowedFileTypesByRuleId = this.parseStoredRuleAllowedFileTypes(setConfig.ruleAllowedFileTypes);
        const tagValuesByRuleId = this.parseStoredRuleTagValues(setConfig.ruleTagValues);
        this.rules = loadedRules.map(({overview, nodeParams, manifest, ...rule}) => ({
          ...(rule as HybridRule),
          // Keine gespeicherte Einschränkung (z. B. neu angelegte Regel) ⇒ "Alle erlaubt" statt einer
          // leer angezeigten Auswahl. Reines Anzeige-Default; beim Speichern wird nur eine tatsächlich
          // eingeschränkte Auswahl persistiert (siehe ruleAllowedFileTypes/-DocumentClasses beim Save).
          allowedDocumentClasses: allowedDocumentClassesByRuleId[rule.id] ?? this.getEffectiveAllowedDocumentClasses(),
          allowedFileTypes: allowedFileTypesByRuleId[rule.id] ?? this.getEffectiveAllowedFileTypes(),
          tagValues: tagValuesByRuleId[rule.id] ?? {},
        }));
        this.ruleVisibleParamIds = Object.fromEntries(
          loadedRules.map(rule => [rule.id, this.computeRuleVisibleSet(rule.nodeParams ?? [])]),
        );
        this.reconcileRuleAllowedSelections();
        this.ruleOverviewByRuleId = Object.fromEntries(
          loadedRules.map(rule => [
            rule.id,
            (rule.overview as RuleOverviewData) ?? {inputItems: [], checkSteps: [], resultItems: [], resultScope: ''},
          ]),
        );
        this.ruleShortCodes = Object.fromEntries(
          this.rules.map(rule => [rule.id, this.normalizeRuleShortCode(this.ruleShortCodes[rule.id])])
        );
        this.markSaved();
        this.loadPendingProposalIfAny();
        this.openPendingRuleTest();
      });
    });
  }

  /**
   * Baut die Set-Konfiguration, die `save()` nach `manifest.trigger.config.hybridEditor` schreibt, aus
   * den lebenden Formular-/Komponentenfeldern – als reine Funktion, ohne Seiteneffekt. Herausgezogen aus
   * `save()`, damit `buildHybridDocumentSnapshot()` (Grundlage für den Chat-Vorschlags-Diff) dieselbe Logik nutzt, statt sie zu duplizieren.
   */
  private buildSetConfig(f: Record<string, any>, normalizedRules: HybridRule[]): Record<string, unknown> {
    return {
      allowedDocumentClasses: this.parseStoredDocumentClasses(f['allowedDocumentClasses']),
      allowedFileTypes: this.serializeAllowedFileTypes(f['allowedFileTypes']),
      checkMode: f['checkMode'] ?? 'SINGLE',
      inputParameters: this.inputParameters,
      checkParameters: this.checkParameters,
      tags: this.tags,
      eingabeparameter: this.eingabeparameter,
      pruefebene: this.pruefebene,
      pruefebeneFilter: this.pruefebene !== 'gesamtdokument' ? this.pruefebeneFilter : '',
      kiModel: this.aiModel,
      identifikatorParamId: this.identifikatorParamId,
      // Anzeige- und Dokumentationswert, KEIN Laufzeitvertrag: die Set-Konfiguration eines
      // hybriden Sets liegt im Manifest (trigger.config.hybridEditor) und wird zur Laufzeit nicht
      // gelesen – siehe RuleGroupResolver.java:320-325. Das Antwortschema der Prüfung kommt aus
      // den `ausgabeparameter` des jeweiligen KI-Anweisung-Knotens, und das bleibt so gewollt
      // (jede KI-Anweisung hat ihre eigenen). Wer hier eine Wirkung sucht: es gibt keine.
      outputParameters: this.outputParameters.filter(param => param.modus !== 'Prüfparameter'),
      ruleShortCodes: this.normalizeRuleShortCodeMap(this.ruleShortCodes),
      ruleAllowedDocumentClasses: Object.fromEntries(
        normalizedRules.map(rule => [rule.id, this.parseStoredDocumentClasses(rule.allowedDocumentClasses)]),
      ),
      ruleAllowedFileTypes: Object.fromEntries(
        normalizedRules.map(rule => [rule.id, this.serializeAllowedFileTypes(rule.allowedFileTypes)]),
      ),
      ruleTagValues: Object.fromEntries(
        normalizedRules.map(rule => [rule.id, rule.tagValues ?? {}]),
      ),
    };
  }

  /**
   * Schreibt einen Vorschlag in den Formularzustand, OHNE zu speichern (Änderungsmodell: Vorschlag →
   * Nutzer bestätigt explizit). Betrifft NUR Set-Felder und Felder bestehender Regeln – `manifest.steps`
   * selbst bleibt unverändert (siehe Struktur-Guard in `loadAndApplyProposal`), Regeln werden also nie
   * angelegt oder gelöscht.
   */
  private applyHybridDocumentToEditor(doc: HybridProposalDocument): void {
    // restoreEingabeparameter legt die Zeilen als neue Objekte an; offene Zeilen-Entscheidungen eines
    // Vorschlags müssen danach an diesen hängen, nicht an den alten.
    const previousRows = this.captureRowLists();
    const setConfig = doc.setConfig ?? {};
    const allowedDocumentClasses = this.parseStoredDocumentClasses(setConfig['allowedDocumentClasses']);
    this.form.patchValue({
      name: doc.name,
      description: doc.description,
      allowedDocumentClasses: allowedDocumentClasses.length
        ? allowedDocumentClasses
        : this.availableDocumentClasses.map(documentClass => documentClass.id),
      allowedFileTypes: this.parseStoredAllowedFileTypes(setConfig['allowedFileTypes']),
      checkMode: setConfig['checkMode'] ?? this.form.value.checkMode,
    }, {emitEvent: false});

    if (Array.isArray(setConfig['inputParameters'])) this.inputParameters = setConfig['inputParameters'] as any;
    if (Array.isArray(setConfig['checkParameters'])) this.checkParameters = setConfig['checkParameters'] as any;
    if (Array.isArray(setConfig['tags'])) this.tags = setConfig['tags'] as any;
    this.eingabeparameter = this.restoreEingabeparameter(setConfig);
    this.pruefebene = normalizePruefebene(setConfig['pruefebene']);
    if (setConfig['kiModel']) this.aiModel = setConfig['kiModel'] as string;
    const proposedIdentifikator = typeof setConfig['identifikatorParamId'] === 'string' ? setConfig['identifikatorParamId'] as string : '';
    this.identifikatorParamId = this.eingabeparameter.some(param => param.id === proposedIdentifikator)
      ? proposedIdentifikator
      : (this.eingabeparameter[0]?.id ?? '');
    this.pruefebeneFilter = this.pruefebene !== 'gesamtdokument' && typeof setConfig['pruefebeneFilter'] === 'string'
      ? setConfig['pruefebeneFilter'] as string
      : '';
    // Prüfparameter-Einträge sind nicht Teil von setConfig.outputParameters (siehe buildSetConfig) –
    // beim Anwenden erhalten, nur die vorgeschlagenen Nicht-Prüfparameter-Einträge übernehmen.
    const keptRuntimeParams = this.outputParameters.filter(param => param.modus === 'Prüfparameter');
    const proposedOutputParams = Array.isArray(setConfig['outputParameters']) ? setConfig['outputParameters'] as SetOutputParameter[] : [];
    this.outputParameters = migrateOutputParameterOptions([...proposedOutputParams, ...keptRuntimeParams]);

    const proposedById = new Map(doc.rules.map(rule => [rule.id, rule]));
    this.rules = this.rules.map(rule => {
      const proposed = proposedById.get(rule.id);
      if (!proposed) return rule;
      return {
        ...rule,
        name: proposed.name,
        description: proposed.description,
        allowedDocumentClasses: proposed.allowedDocumentClasses?.length
          ? proposed.allowedDocumentClasses
          : this.getEffectiveAllowedDocumentClasses(),
        allowedFileTypes: this.parseStoredRuleAllowedFileTypeSelection(proposed.allowedFileTypes),
        tagValues: proposed.tagValues ?? {},
      };
    });
    this.ruleShortCodes = {
      ...this.normalizeRuleShortCodeMap(setConfig['ruleShortCodes']),
      ...Object.fromEntries(doc.rules.map(rule => [rule.id, this.normalizeRuleShortCode(rule.shortCode)])),
    };
    this.reconcileRuleAllowedSelections();
    this.rebindRowLists(previousRows);
  }

  /**
   * Regeln, die der aktuelle Vorschlag anlegt oder löscht – dürfen NIE automatisch angewendet werden
   * (siehe Domain-Dokument: `createRule`/`duplicateRule`/`confirmDelete` persistieren im Hybrid-Editor
   * sofort, ein Vorschlag darf diese Pfade nicht auslösen). Der Vorschlag bleibt serverseitig PENDING,
   * der Nutzer sieht nur einen Hinweis – kein automatisches Discard, damit die Chat-Historie erhalten
   * bleibt (siehe „Offene Punkte" im Umsetzungsplan).
   */
  private hasUnsupportedStructureChange(diff: HybridDiff): boolean {
    return diff.ruleChanges.added.length > 0 || diff.ruleChanges.removed.length > 0;
  }

  /**
   * Nimmt den offenen Vorschlag zurück: mit `'current'` nur seine noch offenen Änderungen (eigene
   * Änderungen bleiben), mit `'snapshot'` zurück auf den Stand von vor dem Vorschlag plus einzeln
   * Übernommenes. Bewusst KEIN `markSaved()`: war der Editor vorher bereits dirty (eigene, noch
   * ungespeicherte Änderungen), soll das nach dem Verwerfen so bleiben.
   */
  private revertPendingProposal(base: 'current' | 'snapshot' = 'current'): void {
    if (this.preProposalSnapshot) {
      this.applyHybridDocumentToEditor(base === 'current' && this.proposalDiff
        ? this.buildRevertOpenChangesDocument(this.preProposalSnapshot, this.proposalDiff)
        : this.buildPartialRevertDocument(this.preProposalSnapshot));
    }
    this.pendingProposal = null;
    this.proposalDiff = null;
    this.preProposalSnapshot = null;
    this.individuallyAcceptedPaths.clear();
    this.individuallyAcceptedRulePaths.clear();
    this.ruleEntryDecisions.reset();
    this.resetRowLists();
  }

  /** Übersteht ein PENDING-Vorschlag einen Reload, statt beim erneuten Laden verloren zu gehen. */
  private loadPendingProposalIfAny(): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getLatestPending(this.rulesetId, projectId).subscribe({
      next: proposal => {
        if (proposal) this.applyProposal(proposal);
      },
      error: err => console.error('Offener Vorschlag konnte nicht geladen werden:', err),
    });
  }

  /** Reagiert auf das `mycheckProposalReceived`-Signal des Edit-Modus-Chats. */
  private loadAndApplyProposal(proposalId: string): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getById(this.rulesetId, proposalId, projectId).subscribe({
      next: proposal => {
        if (proposal.status === 'PENDING') this.applyProposal(proposal);
      },
      error: err => console.error('Vorschlag konnte nicht geladen werden:', err),
    });
  }

  navigateBack(): void {
    this.savingInProgress = false;
    this.router.navigate(['../../'], {relativeTo: this.route});
  }

  private markSaved(): void {
    this.initialState = this.serializeEditorState();
  }

  private serializeEditorState(): string {
    return JSON.stringify({
      form: this.form.getRawValue(),
      rules: this.rules,
      ruleShortCodes: this.ruleShortCodes,
      inputParameters: this.inputParameters,
      checkParameters: this.checkParameters,
      tags: this.tags,
      eingabeparameter: this.eingabeparameter,
      pruefebene: this.pruefebene,
      identifikatorParamId: this.identifikatorParamId,
      pruefebeneFilter: this.pruefebeneFilter,
      aiModel: this.aiModel,
      outputParameters: this.outputParameters,
      ruleVisibleParamIds: Object.fromEntries(
        Object.entries(this.ruleVisibleParamIds).map(([id, ids]) => [id, [...ids].sort()]),
      ),
    });
  }

  /**
   * Gescheiterter Speichervorgang: Sperre der Reload-Kaskade lösen und den Fehler melden.
   * Ohne das Lösen der Sperre blieben Änderungen anderer Quellen bis zum Neuladen unsichtbar.
   */
  private onSaveFailed(message: string, err: unknown): void {
    this.savingInProgress = false;
    console.error(`${message}:`, err);
    this.toastService.error(message);
  }

  createRule(): void {
    if (!this.canAddRule) {
      this.showRuleCreationWarning();
      return;
    }
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!this.adminGlobal && !projectId) return;

    // Neue Regel erbt die Set-Eingabeparameter direkt (sonst wäre der eingangsparameter-Knoten
    // im Workflow-Editor leer). NEUE Regel ⇒ alle Parameter verdeckt, nur Identifikator sichtbar.
    // (Bestehende Regeln zeigen beim Erst-Abgleich via reconcile hingegen alle Parameter.)
    const initialParams = this.setParamDefs().map(sp => ({
      id: sp.id,
      name: sp.name,
      beschreibung: sp.beschreibung ?? '',
      typ: sp.dataType ?? 'Text',
      optionen: sp.options ?? [],
      hidden: sp.id !== this.identifikatorParamId,
    }));
    const ruleManifest = {
      name: '',
      version: 'v1',
      description: '',
      rulesetType: 'HYBRID',
      trigger: {
        nodeId: 'trigger_0',
        moduleId: 'eingangsparameter',
        config: {
          parameter: initialParams,
          pruefebene: this.pruefebene,
          modell: this.aiModel,
          // Neue Regel erbt die Set-Auswahl ⇒ keine Einschränkung ⇒ „Alle Dokumentenklassen“.
          erlaubte_dokumentenklassen: [],
          identifikatorParamId: this.identifikatorParamId,
          outputSchema: buildParameterOutputSchema({parameter: initialParams}),
        },
      },
      steps: [],
    };

    const createChild$ = this.adminGlobal
      ? this.rulesetService.createGlobalHybridDefinition(ruleManifest as any, 'HYBRID_RULE')
      : this.flowService.saveDefinition(ruleManifest as any, projectId!, 'HYBRID_RULE');
    createChild$.subscribe({
      next: newDef => {
        const ruleId = newDef.id ?? '';

        const newStep = {
          nodeId: ruleId,
          moduleId: 'HYBRID_RULE',
          workflowDefinitionId: ruleId,
          inputs: { '__name': '' },
        };
        this.manifest.steps = [...(this.manifest.steps ?? []), newStep];
        this.manifest.trigger = this.manifest.trigger ?? {
          nodeId: 'trigger_0',
          moduleId: 'MANUAL_TRIGGER',
          config: {},
        };

        this.persistDefinition(this.ruleset.id, this.manifest).subscribe({
          next: () => {
            // allowedDocumentClasses/allowedFileTypes bleiben hier leer: das nach dem Persistieren
            // ausgelöste definitionUpdated$ lädt den Regelsatz ohnehin sofort neu (siehe loadRuleset()),
            // wo eine fehlende Einschränkung bereits korrekt als "Alle erlaubt" aufgelöst wird.
            const newRule: HybridRule = {
              id: ruleId,
              name: '',
              description: '',
              moduleCount: 0,
              allowedDocumentClasses: [],
              allowedFileTypes: [],
            };
            this.rules = [...this.rules, newRule];
            this.ruleOverviewByRuleId = {
              ...this.ruleOverviewByRuleId,
              [ruleId]: {inputItems: [], checkSteps: [], resultItems: [], resultScope: ''},
            };
            // Sichtbarkeit initialisieren: neue Regel nutzt nur den Identifikator (alle anderen versteckt).
            this.ruleVisibleParamIds = {...this.ruleVisibleParamIds, [ruleId]: new Set<string>()};
            this.ruleset.ruleCount = this.rules.length;
            this.selectRule(newRule);
          },
          error: err => console.error('Ruleset aktualisieren fehlgeschlagen:', err),
        });
      },
      error: err => console.error('Prüfregel erstellen fehlgeschlagen:', err),
    });
  }

  openRuleEditor(rule: HybridRule): void {
    // Read-only in die Einzelregel (workflow-editor) durchreichen.
    const queryParams = this.readOnly ? {readOnly: 'true'} : {};
    const navigate = () => this.router.navigate([rule.id], {relativeTo: this.route, queryParams});

    // Schreibend: erst das Regelset speichern, dann springen. Ohne das gingen hier gemachte
    // Set-Änderungen (Eingabeparameter, Prüfebene, KI-Modell, Regelnamen …) verloren — und der
    // Workflow-Editor arbeitete zudem auf einem veralteten eingangsparameter-Knoten weiter.
    if (this.readOnly) {
      navigate();
      return;
    }
    this.save(navigate);
  }

  duplicateRule(rule: HybridRule): void {
    if (!this.canCreateRule) {
      this.showRuleLimitWarning();
      return;
    }
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!this.adminGlobal && !projectId) return;

    this.loadDefinition(rule.id).subscribe({
      next: sourceDef => {
        const sourceManifest = this.flowService.parseManifest(sourceDef);
        const duplicatedManifest = {
          ...sourceManifest,
          name: `${rule.name || 'Prüfregel'} (Kopie)`,
          description: rule.description ?? sourceManifest.description ?? '',
        };
        const overview = this.buildRuleOverviewFromManifest(duplicatedManifest);

        const createCopy$ = this.adminGlobal
          ? this.rulesetService.createGlobalHybridDefinition(duplicatedManifest as any, 'HYBRID_RULE')
          : this.flowService.saveDefinition(duplicatedManifest as any, projectId!, 'HYBRID_RULE');
        createCopy$.subscribe({
          next: duplicatedDef => {
            const duplicatedRuleId = duplicatedDef.id ?? '';
            if (!duplicatedRuleId) {
              console.error('Duplizierte Prüfregel hat keine ID erhalten.');
              return;
            }

            const duplicatedRuleName = duplicatedDef.name ?? duplicatedManifest.name ?? 'Prüfregel (Kopie)';
            const duplicatedStep = {
              nodeId: duplicatedRuleId,
              moduleId: 'HYBRID_RULE',
              workflowDefinitionId: duplicatedRuleId,
              inputs: {
                '__name': duplicatedRuleName,
                '__description': duplicatedManifest.description ?? '',
                '__shortCode': this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]),
              },
            };

            this.manifest.steps = [...(this.manifest.steps ?? []), duplicatedStep];
            this.persistDefinition(this.ruleset.id, this.manifest).subscribe({
              next: () => {
                const duplicatedRule: HybridRule = {
                  id: duplicatedRuleId,
                  name: duplicatedRuleName,
                  description: duplicatedManifest.description ?? '',
                  moduleCount: this.flowService.getModuleCountFromDef(duplicatedDef),
                  allowedDocumentClasses: [...(rule.allowedDocumentClasses ?? [])],
                  allowedFileTypes: [...(rule.allowedFileTypes ?? [])],
                };
                this.rules = [...this.rules, duplicatedRule];
                this.ruleOverviewByRuleId = {
                  ...this.ruleOverviewByRuleId,
                  [duplicatedRuleId]: overview,
                };
                this.ruleShortCodes = {
                  ...this.ruleShortCodes,
                  [duplicatedRuleId]: this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]),
                };
                this.ruleset.ruleCount = this.rules.length;
                this.selectRule(duplicatedRule);
              },
              error: err => console.error('Fehler beim Aktualisieren des Regelsets nach Duplizieren:', err),
            });
          },
          error: err => console.error('Fehler beim Speichern der duplizierten Prüfregel:', err),
        });
      },
      error: err => console.error('Fehler beim Laden der Prüfregel zum Duplizieren:', err),
    });
  }

  addInputParameter(): void {
    this.inputParameters = [...this.inputParameters, {
      id: crypto.randomUUID(),
      name: '',
      dataType: 'Text',
      necessity: 'Optional'
    }];
  }

  removeInputParameter(index: number): void {
    this.inputParameters = this.inputParameters.filter((_, i) => i !== index);
  }

  // ─── Eingabeparameter (Set-Ebene) ───────────────────────────────────────────

  /** Eingabeparameter mit Namen — als Optionen für den Identifikator-Select. */
  get identifikatorOptions(): SetEingabeparameter[] {
    return this.eingabeparameter.filter(param => (param.name ?? '').trim().length > 0);
  }

  isIdentifikator(param: SetEingabeparameter): boolean {
    return !!this.identifikatorParamId && param.id === this.identifikatorParamId;
  }

  addEingabeparameter(): void {
    this.eingabeparameter = [
      ...this.eingabeparameter,
      {id: crypto.randomUUID(), name: '', beschreibung: '', dataType: 'Text'},
    ];
  }

  removeEingabeparameter(index: number): void {
    const removed = this.eingabeparameter[index];
    this.eingabeparameter = this.eingabeparameter.filter((_, i) => i !== index);
    if (removed && removed.id === this.identifikatorParamId) {
      this.identifikatorParamId = this.eingabeparameter[0]?.id ?? '';
    }
  }

  /** Identifikator setzen. Der Identifikator ist immer 'Text' (Positionsnummern mit führenden Nullen/Punkten). */
  setIdentifikator(paramId: string): void {
    this.identifikatorParamId = paramId;
    this.eingabeparameter = this.eingabeparameter.map(param =>
      param.id === paramId ? {...param, dataType: 'Text'} : param,
    );
  }

  updateEingabeparameterDataType(param: SetEingabeparameter, value: string): void {
    // Der Identifikator bleibt immer 'Text'.
    const dataType = this.isIdentifikator(param) ? 'Text' : this.normalizeDataType(value);
    this.eingabeparameter = this.eingabeparameter.map(p =>
      // Wechsel WEG von 'Auswahl' räumt die Optionen mit auf: sie hätten keine Bedeutung mehr,
      // würden aber weiter gespeichert und beim Zurückwechseln als Altstand wieder auftauchen.
      p.id === param.id
        ? {...p, dataType, options: needsOptions({dataType}) ? (p.options ?? []) : undefined}
        : p,
    );
  }

  onPruefebeneChange(value: string | string[]): void {
    const next = normalizePruefebene(Array.isArray(value) ? value[0] : value);
    if (next === this.pruefebene) return;
    this.pruefebene = next;
    this.applyPruefebeneDefaults(next);
  }

  /**
   * Schlägt beim Wechsel der Prüfebene passende Eingabeparameter vor (z. B. LV-Position →
   * Ordnungszahl/Kurztext/…). Ersetzt dabei immer nur die zuvor vorgeschlagenen Preset-Parameter
   * (`isPreset`) durch die Vorschläge der neuen Prüfebene; vom Nutzer angelegte/umbenannte
   * Parameter bleiben unverändert erhalten. Bei Gesamtdokument gibt es keine Vorschläge, keinen
   * Identifikator und keinen Prüfebene-Filter.
   */
  private applyPruefebeneDefaults(pruefebene: Pruefebene): void {
    const customParams = this.eingabeparameter.filter(param => !param.isPreset);
    const defaultNames = PRUEFEBENE_DEFAULT_PARAMETERS[pruefebene];
    const presetParams: SetEingabeparameter[] = defaultNames.map(name => ({
      id: crypto.randomUUID(), name, beschreibung: '', dataType: 'Text', isPreset: true,
    }));
    this.eingabeparameter = [...presetParams, ...customParams];

    if (pruefebene === 'gesamtdokument') {
      this.identifikatorParamId = '';
      this.pruefebeneFilter = '';
      return;
    }
    if (!this.eingabeparameter.some(param => param.id === this.identifikatorParamId)) {
      this.identifikatorParamId = this.eingabeparameter[0]?.id ?? '';
    }
  }

  private normalizeDataType(value: unknown): string {
    const candidate = String(value ?? '').trim();
    return (this.eingabeParameterTypes as readonly string[]).includes(candidate) ? candidate : 'Text';
  }

  /** Lädt die Set-Eingabeparameter; migriert bei Bedarf aus Alt-`inputParameters` (nur name/dataType). */
  private restoreEingabeparameter(config: any): SetEingabeparameter[] {
    const raw = Array.isArray(config?.eingabeparameter) ? config.eingabeparameter : null;
    if (raw && raw.length) {
      return raw.map((p: any) => ({
        id: typeof p?.id === 'string' && p.id ? p.id : crypto.randomUUID(),
        name: String(p?.name ?? ''),
        beschreibung: String(p?.beschreibung ?? ''),
        dataType: this.normalizeDataType(p?.dataType ?? p?.typ),
        isPreset: !!p?.isPreset,
        // Ohne die Optionen stünde ein gespeicherter Auswahl-Parameter nach dem Laden ohne Werte da,
        // und getMissingOptionMessages() blockierte jedes weitere Speichern.
        ...this.restoreOptions(p),
      }));
    }
    const legacy = Array.isArray(config?.inputParameters) ? config.inputParameters : [];
    return legacy.map((p: any) => ({
      id: typeof p?.id === 'string' && p.id ? p.id : crypto.randomUUID(),
      name: String(p?.name ?? ''),
      beschreibung: '',
      dataType: this.normalizeDataType(p?.dataType ?? p?.typ),
      ...this.restoreOptions(p),
    }));
  }

  /** Auswahl-Optionen eines gespeicherten Parameters, wie der Workflow-Editor sie liest; ohne Liste kein Feld. */
  private restoreOptions(p: any): { options?: string[] } {
    return Array.isArray(p?.options) ? {options: p.options.map((o: any) => String(o ?? ''))} : {};
  }

  // ─── Per-Regel-Sichtbarkeit der Set-Eingabeparameter ────────────────────────

  /** Set-Eingabeparameter mit Namen — Grundmenge für die Regel-Ansicht. */
  get ruleVisibilityParameters(): SetEingabeparameter[] {
    return this.eingabeparameter.filter(param => (param.name ?? '').trim().length > 0);
  }

  /** Nur die in der ausgewählten Regel sichtbar geschalteten Parameter (inkl. Identifikator). */
  get overviewVisibleParameters(): SetEingabeparameter[] {
    return this.ruleVisibilityParameters.filter(param => this.isRuleParamVisible(param.id));
  }

  /** Identifikator ist gesperrt (immer genutzt, Report-Konsistenz). */
  isRuleParamLocked(paramId: string): boolean {
    return !!this.identifikatorParamId && paramId === this.identifikatorParamId;
  }

  isRuleParamVisible(paramId: string): boolean {
    if (this.isRuleParamLocked(paramId)) return true;
    if (!this.selectedRuleId) return false;
    return this.ruleVisibleParamIds[this.selectedRuleId]?.has(paramId) ?? false;
  }

  toggleRuleParamVisibility(paramId: string): void {
    if (!this.selectedRuleId || this.isRuleParamLocked(paramId)) return;
    const next = new Set(this.ruleVisibleParamIds[this.selectedRuleId] ?? []);
    if (next.has(paramId)) {
      next.delete(paramId);
    } else {
      next.add(paramId);
    }
    this.ruleVisibleParamIds = {...this.ruleVisibleParamIds, [this.selectedRuleId]: next};
  }

  /** Liest die `parameter`-Liste des eingangsparameter-Trigger-Knotens eines Regel-Manifests. */
  private extractNodeParams(manifest: any): any[] {
    const params = manifest?.trigger?.config?.parameter;
    return Array.isArray(params) ? params : [];
  }

  /**
   * Effektive Sichtbarkeit einer Regel — spiegelt {@link reconcileEingangParameters}: bekannter
   * Parameter behält seinen `hidden`-Zustand; beim Erst-Abgleich (Knoten trägt noch keine Set-IDs)
   * sind alle sichtbar; nachträglich ergänzte Set-Parameter gelten als versteckt. Der Identifikator
   * wird separat immer als sichtbar behandelt und ist hier nicht enthalten.
   */
  private computeRuleVisibleSet(nodeParams: any[]): Set<string> {
    const byId = new Map<string, any>();
    for (const param of nodeParams ?? []) {
      if (param && typeof param.id === 'string' && param.id) byId.set(param.id, param);
    }
    const alreadySynced = this.eingabeparameter.some(sp => byId.has(sp.id));
    const result = new Set<string>();
    for (const sp of this.eingabeparameter) {
      if (sp.id === this.identifikatorParamId) continue;
      const prev = byId.get(sp.id);
      const hidden = prev ? prev.hidden === true : !alreadySynced ? false : true;
      if (!hidden) result.add(sp.id);
    }
    return result;
  }

  openOptionsPopup(kind: 'tag' | 'output' | 'input', target: OptionsHolder): void {
    this.optionsPopupKind = kind;
    this.optionsPopupTarget = target;
    this.optionsPopupLabel = String(target?.name ?? '').trim();
    this.optionsPopupOptions = target?.options ?? [];
    this.optionsPopupVisible = true;
  }

  /**
   * Set-Modell in die Settings jedes KI-Anweisung-Knotens schreiben.
   *
   * Das Modell wird EINMAL pro Regelset gepflegt; im Knoten-Panel steht es nur als read-only
   * Badge. Bis hierher landete der Set-Wert lediglich in der Trigger-Config (eingangsparameter),
   * die KI-Anweisung behielt dagegen ihr eigenes, beim Anlegen eingefrorenes Modell — eine
   * Änderung am Set blieb also ohne Wirkung.
   */
  private applySetAiModelToSteps(manifest: any): void {
    if (!this.aiModel) return;
    for (const step of manifest?.steps ?? []) {
      if ((step?.moduleId ?? '').toLowerCase() !== AI_INSTRUCTION_MODULE_ID) continue;
      step.settings = {...(step.settings ?? {}), modell: this.aiModel};
    }
  }

  addCheckParameter(): void {
    this.checkParameters = [...this.checkParameters, {
      id: crypto.randomUUID(),
      name: 'Neuer Prüfparameter',
      dataType: 'Text',
      necessity: 'Optional'
    }];
  }

  confirmDelete(): void {
    if (!this.ruleToDelete) return;
    const rule = this.ruleToDelete;

    this.manifest.steps = (this.manifest.steps ?? []).filter((s: any) => s.nodeId !== rule.id);

    this.persistDefinition(this.ruleset.id, this.manifest).subscribe({
      next: () => {
        this.flowService.archiveDefinition(rule.id).subscribe();
        this.rules = this.rules.filter(r => r.id !== rule.id);
        const {[rule.id]: _, ...rest} = this.ruleShortCodes;
        this.ruleShortCodes = rest;
        const {[rule.id]: _overview, ...overviewRest} = this.ruleOverviewByRuleId;
        this.ruleOverviewByRuleId = overviewRest;
        if (this.selectedRuleId === rule.id) {
          this.selectedRuleId = null;
          this.selectedSection = 'general';
        }
        this.ruleset.ruleCount = this.rules.length;
        this.ruleToDelete = null;
      },
      error: err => {
        console.error('Fehler beim Löschen der Prüfregel:', err);
        this.ruleToDelete = null;
      },
    });
  }

  removeCheckParameter(index: number): void {
    this.checkParameters = this.checkParameters.filter((_, i) => i !== index);
  }

  addTag(): void {
    this.tags = [...this.tags, {
      id: crypto.randomUUID(),
      name: '',
      dataType: 'Auswahl',
      necessity: 'Optional',
      options: []
    }];
  }

  removeTag(index: number): void {
    this.tags = this.tags.filter((_, i) => i !== index);
  }

  addOutputParameter(): void {
    this.outputParameters = [...this.outputParameters, {modus: 'KI-Generiert', name: '', wert: '', dateityp: 'Text'}];
  }

  removeOutputParameter(index: number): void {
    this.outputParameters = this.outputParameters.filter((_, i) => i !== index);
  }

  onOptionsPopupClosed(): void {
    this.optionsPopupVisible = false;
    this.optionsPopupKind = null;
    this.optionsPopupTarget = null;
  }

  onOptionsPopupSaved(options: string[]): void {
    if (this.optionsPopupTarget) {
      this.optionsPopupTarget.options = options;
      switch (this.optionsPopupKind) {
        case 'tag':
          this.tags = [...this.tags];
          break;
        case 'output':
          this.outputParameters = [...this.outputParameters];
          break;
        case 'input':
          this.eingabeparameter = [...this.eingabeparameter];
          break;
      }
    }
    this.onOptionsPopupClosed();
  }

  /** Chips-Vorschau der konfigurierten Optionen in der Parameterzeile. */
  optionsPreview(param: OptionsHolder | null | undefined): string[] {
    return (param?.options ?? []).filter(option => String(option ?? '').trim());
  }

  /** Ob der Parameter eine Optionsliste braucht (Datentyp „Auswahl"). */
  needsOptions(param: { dataType?: string; dateityp?: string } | null | undefined): boolean {
    return needsOptions(param);
  }

  /** Set-Eingabeparameter als Parameter-Definitionen (Projektion für den eingangsparameter-Knoten). */
  private setParamDefs(): SetParamDef[] {
    return this.eingabeparameter
      .filter(param => (param.name ?? '').trim().length > 0)
      .map(param => ({
        id: param.id,
        name: param.name,
        beschreibung: param.beschreibung,
        dataType: param.dataType,
        options: param.options ?? [],
      }));
  }

  /**
   * Auswahl-Parameter ohne Optionen. Blockiert das Speichern.
   *
   * Für hybride Sets ist das die EINZIGE Prüfung: der `RulesetCompletenessValidator` im Backend
   * sieht diese Konfiguration nicht, weil sie im Manifest liegt (`trigger.config.hybridEditor`)
   * und nicht in einer `configJson`-Spalte.
   */
  private getMissingOptionMessages(): string[] {
    return collectMissingOptionMessages([
      {label: 'Ausgabeparameter', params: this.outputParameters},
      {label: 'Eingabeparameter', params: this.eingabeparameter},
      {label: 'Tag', params: this.tags},
    ]);
  }

  // Definition-Zugriffe: im Admin-Modus projektlos über die globalen Endpunkte, sonst projekt-scoped.
  private persistDefinition(id: string, manifest: any) {
    return this.adminGlobal
      ? this.rulesetService.updateGlobalHybridDefinition(id, manifest)
      : this.flowService.updateDefinition(id, manifest, this.stateConfigService.selectedProject!.id!);
  }

  private getSetConfig(config: unknown): any {
    if (typeof config === 'string') {
      try {
        return JSON.parse(config);
      } catch {
        return {};
      }
    }
    if (config && typeof config === 'object') {
      return config;
    }
    return {};
  }

  private loadDefinition(id: string) {
    return this.adminGlobal
      ? this.flowService.getDefinition(id)
      : this.flowService.getDefinition(id, this.stateConfigService.selectedProject?.id);
  }

  private normalizeRuleShortCode(value: unknown): string {
    return String(value ?? '').trim().slice(0, HybridEditorComponent.RULE_SHORT_CODE_MAX_LENGTH);
  }

  private hasUneditedEmptyRule(): boolean {
    return this.rules.some(rule =>
      this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]).length === 0
      && (rule.name ?? '').trim().length === 0
      && (rule.description ?? '').trim().length === 0,
    );
  }

  private showRuleCreationWarning(): void {
    if (!this.canCreateRule) {
      this.showRuleLimitWarning();
      return;
    }
    this.toastService.warning('Bitte bearbeite zuerst die neue leere Prüfregel.');
  }

  private showRuleLimitWarning(): void {
    this.toastService.warning(`Es sind maximal ${this.MAX_RULES} Prüfregeln möglich.`);
  }

  updateAllowedDocumentClasses(value: string[] | string): void {
    if (this.suppressNextAllowedDocumentClassesChange) {
      this.suppressNextAllowedDocumentClassesChange = false;
      return;
    }
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    const nextValue = this.normalizeMultiSelectValue(value, this.getAllowedDocumentClassesValue(), allDocumentClassIds);
    // Leere Auswahl = keine Dokumentenklasse erlaubt: dann findet der myCheck keine Datei mehr.
    // Das Feld faellt deshalb auf „alle Klassen" zurueck, statt leer zu bleiben.
    this.form.get('allowedDocumentClasses')?.setValue(nextValue.length ? nextValue : [...allDocumentClassIds]);
    this.reconcileRuleAllowedSelections();
  }

  updateAllowedFileTypes(value: string[] | string): void {
    if (this.suppressNextAllowedFileTypesChange) {
      this.suppressNextAllowedFileTypesChange = false;
      return;
    }
    const nextValue = this.normalizeMultiSelectValue(value, this.getAllowedFileTypesValue(), this.availableFileTypes);
    this.form.get('allowedFileTypes')?.setValue(nextValue);
    this.reconcileRuleAllowedSelections();
  }

  /**
   * „Alle erlauben" schaltet nur EIN: eine leere Auswahl waere eine Einschraenkung auf nichts,
   * mit der der myCheck keine Datei mehr annimmt (siehe updateAllowedDocumentClasses).
   */
  toggleAllAllowedDocumentClasses(event?: MouseEvent): void {
    event?.stopPropagation();
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    if (allDocumentClassIds.length === 0) return;
    this.suppressNextAllowedDocumentClassesChange = true;
    this.form.get('allowedDocumentClasses')?.setValue([...allDocumentClassIds]);
    this.reconcileRuleAllowedSelections();
  }

  toggleAllAllowedFileTypes(event?: MouseEvent): void {
    event?.stopPropagation();
    const nextValue = this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes) ? [] : [...this.availableFileTypes];
    this.suppressNextAllowedFileTypesChange = true;
    this.form.get('allowedFileTypes')?.setValue(nextValue);
    this.reconcileRuleAllowedSelections();
  }

  getAllowedDocumentClassesSelectionValue(): string[] {
    // Gecacht, weil das Ergebnis an [value] haengt (siehe memo.ts); Inhalt als
    // Schluessel, damit Formular-Aenderungen sicher ankommen.
    const value = this.getAllowedDocumentClassesValue();
    return this.memo('allowedDocumentClassesSelection', [contentKey(value)], () => [...value]);
  }

  getAllowedFileTypesSelectionValue(): string[] {
    // Wie getAllowedDocumentClassesSelectionValue.
    const value = this.getAllowedFileTypesValue();
    return this.memo('allowedFileTypesSelection', [contentKey(value)], () => [...value]);
  }

  getAllowedDocumentClassesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  getAllowedFileTypesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  areAllAllowedFileTypesSelected(): boolean {
    return this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes);
  }

  /**
   * Auch eine leere Auswahl gilt als „alle erlaubt": so liest die Ausfuehrung sie (keine
   * Einschraenkung), und ohne diesen Fall zeigte ein Projekt ganz ohne Dokumentenklassen
   * „Dokumentenklassen auswaehlen", also scheinbar eine Einschraenkung auf nichts.
   */
  areAllAllowedDocumentClassesSelected(): boolean {
    const selected = this.getAllowedDocumentClassesValue();
    if (selected.length === 0) return true;
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    return this.areAllValuesSelected(selected, allDocumentClassIds);
  }

  areAllSelectedRuleAllowedFileTypesSelected(): boolean {
    return this.areAllValuesSelected(this.getSelectedRuleAllowedFileTypesSelectionValue(), this.getRuleAllowedFileTypeOptions());
  }

  areAllSelectedRuleAllowedDocumentClassesSelected(): boolean {
    const availableDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    return this.areAllValuesSelected(this.getSelectedRuleAllowedDocumentClassesSelectionValue(), availableDocumentClassIds);
  }

  updateSelectedRuleAllowedDocumentClasses(value: string[] | string): void {
    if (this.suppressNextSelectedRuleAllowedDocumentClassesChange) {
      this.suppressNextSelectedRuleAllowedDocumentClassesChange = false;
      return;
    }
    if (!this.selectedRuleId) return;
    const selectedRule = this.selectedRule;
    const currentValues = Array.isArray(selectedRule?.allowedDocumentClasses) ? selectedRule.allowedDocumentClasses : [];
    const allValues = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    const nextValue = this.normalizeMultiSelectValue(value, currentValues, allValues);
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, allowedDocumentClasses: nextValue} : rule
    ));
  }

  updateSelectedRuleAllowedFileTypes(value: string[] | string): void {
    if (this.suppressNextSelectedRuleAllowedFileTypesChange) {
      this.suppressNextSelectedRuleAllowedFileTypesChange = false;
      return;
    }
    if (!this.selectedRuleId) return;
    const selectedRule = this.selectedRule;
    const currentValues = Array.isArray(selectedRule?.allowedFileTypes) ? selectedRule.allowedFileTypes : [];
    const allValues = this.getRuleAllowedFileTypeOptions();
    const nextValue = this.normalizeMultiSelectValue(value, currentValues, allValues);
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, allowedFileTypes: nextValue} : rule
    ));
  }

  toggleAllSelectedRuleAllowedDocumentClasses(): void {
    if (!this.selectedRuleId) return;
    const availableDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    const currentValues = this.getSelectedRuleAllowedDocumentClassesSelectionValue();
    const nextValue = this.areAllValuesSelected(currentValues, availableDocumentClassIds) ? [] : [...availableDocumentClassIds];
    this.suppressNextSelectedRuleAllowedDocumentClassesChange = true;
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, allowedDocumentClasses: nextValue} : rule
    ));
  }

  toggleAllSelectedRuleAllowedFileTypes(): void {
    if (!this.selectedRuleId) return;
    const availableFileTypes = this.getRuleAllowedFileTypeOptions();
    const currentValues = this.getSelectedRuleAllowedFileTypesSelectionValue();
    const nextValue = this.areAllValuesSelected(currentValues, availableFileTypes) ? [] : [...availableFileTypes];
    this.suppressNextSelectedRuleAllowedFileTypesChange = true;
    this.rules = this.rules.map(rule => (
      rule.id === this.selectedRuleId ? {...rule, allowedFileTypes: nextValue} : rule
    ));
  }

  getSelectedRuleAllowedDocumentClassesSelectionValue(): string[] {
    return Array.isArray(this.selectedRule?.allowedDocumentClasses) ? [...this.selectedRule.allowedDocumentClasses] : [];
  }

  getSelectedRuleAllowedFileTypesSelectionValue(): string[] {
    return Array.isArray(this.selectedRule?.allowedFileTypes) ? [...this.selectedRule.allowedFileTypes] : [];
  }

  // ─── Regelparameter (tags) pro Regel ────────────────────────────────────────

  /** Ruleset-level Regelparameter (tags) with a usable name — rendered as per-rule dropdowns. */
  get ruleParameterTags(): { id: string; name: string; necessity: string; options?: string[] }[] {
    return this.tags.filter(tag => (tag.name ?? '').trim().length > 0);
  }

  /** Current per-rule value for a Regelparameter (tag). */
  getSelectedRuleTagValue(tagId: string): string {
    return this.selectedRule?.tagValues?.[tagId] ?? '';
  }

  /** Store the per-rule value for a Regelparameter (tag) on the selected rule (persisted on Übernehmen). */
  updateSelectedRuleTagValue(tagId: string, value: string | string[]): void {
    if (!this.selectedRuleId) return;
    const next = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
    this.rules = this.rules.map(rule => {
      if (rule.id !== this.selectedRuleId) return rule;
      const tagValues = {...(rule.tagValues ?? {})};
      if (next) {
        tagValues[tagId] = next;
      } else {
        delete tagValues[tagId];
      }
      return {...rule, tagValues};
    });
  }

  getRuleAllowedDocumentClassOptions(): { id: string; name: string }[] {
    const allowedDocumentClasses = new Set(this.getEffectiveAllowedDocumentClasses());
    return this.availableDocumentClasses.filter(documentClass => allowedDocumentClasses.has(documentClass.id));
  }

  getRuleAllowedFileTypeOptions(): string[] {
    const allowedFileTypes = new Set(this.getEffectiveAllowedFileTypes());
    return this.availableFileTypes.filter(fileType => allowedFileTypes.has(fileType));
  }

  /**
   * Dokumentenklassen einer Regel, wie sie in den eingangsparameter-Knoten geschrieben werden.
   *
   * Deckt die Auswahl alle im Set erlaubten Klassen ab, wird bewusst ein leeres Array gespeichert:
   * der Knoten zeigt dann das „Alle Dokumentenklassen“-Badge, statt eine Aufzählung, die beim
   * Anlegen einer neuen Dokumentenklasse still veralten würde.
   */
  private ruleDocumentClassesForNode(rule: HybridRule): string[] {
    const selected = this.parseStoredDocumentClasses(rule.allowedDocumentClasses);
    const setOptions = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    return this.areAllValuesSelected(selected, setOptions) ? [] : selected;
  }

  private getAllowedDocumentClassesValue(): string[] {
    const value = this.form.get('allowedDocumentClasses')?.value;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private getAllowedFileTypesValue(): string[] {
    const value = this.form.get('allowedFileTypes')?.value;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private getEffectiveAllowedDocumentClasses(): string[] {
    const selected = this.getAllowedDocumentClassesValue();
    if (selected.length > 0) return selected;
    return this.availableDocumentClasses.map(documentClass => documentClass.id);
  }

  private getEffectiveAllowedFileTypes(): string[] {
    const selected = this.getAllowedFileTypesValue();
    if (selected.length > 0) return selected;
    return [...this.availableFileTypes];
  }

  private normalizeMultiSelectValue(value: string[] | string, currentValues: string[], allValues: string[]): string[] {
    const selectedValues = Array.isArray(value) ? value : [value];
    const normalizedValues = [...new Set(selectedValues)];
    const nextAllSelected = this.areAllValuesSelected(normalizedValues, allValues);

    if (nextAllSelected) {
      return [...allValues];
    }

    return normalizedValues.filter(valueItem => allValues.includes(valueItem) || !currentValues.includes(valueItem));
  }

  private areAllValuesSelected(selectedValues: string[], allValues: string[]): boolean {
    return allValues.length > 0 && allValues.every(option => selectedValues.includes(option));
  }

  private normalizeRuleShortCodeMap(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object') {
      return {};
    }
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([ruleId, shortCode]) => [
        ruleId,
        this.normalizeRuleShortCode(shortCode),
      ]),
    );
  }

  private getRuleSortKey(rule: HybridRule): string {
    const shortCode = this.normalizeRuleShortCode(this.ruleShortCodes[rule.id]);
    return shortCode || (rule.name ?? '').trim();
  }

  private buildRuleOverviewFromManifest(manifest: any): RuleOverviewData {
    const inputItems = this.extractOverviewInputItems(manifest?.trigger?.config?.outputSchema?.properties);
    // In Ausführungs-/Flow-Reihenfolge (onSuccess/onThen/onElse), nicht in roher Array-Reihenfolge.
    const checkSteps = manifest?.steps
      ? orderStepsByFlow(manifest)
        .map((step: any) => {
          // KI-Interpretation des Schritts bevorzugen (beim Speichern erzeugt), sonst Modulname.
          const interpreted = typeof step?.inputs?.['__aiInterpretation'] === 'string'
            ? step.inputs['__aiInterpretation'].trim()
            : '';
          return interpreted || this.getStepDisplayName(step);
        })
        .filter((value: string) => value.length > 0)
      : [];

    // Ergebnisse pro Regel: persistierte KI-Interpretation bevorzugen, sonst roh ableiten.
    const persisted = manifest?.trigger?.config?.['__aiResults'];
    const resultItems: RuleResultItem[] = Array.isArray(persisted) ? persisted : deriveRuleResults(manifest);
    const resultScope = ruleResultScope(manifest);

    return {inputItems, checkSteps, resultItems, resultScope};
  }

  private extractOverviewInputItems(properties: unknown): RuleOverviewInputItem[] {
    if (!properties || typeof properties !== 'object') {
      return [];
    }

    return Object.entries(properties as Record<string, any>).map(([key, schema]) => ({
      label: schema?.title || this.toDisplayLabel(key),
      tags: this.getSchemaTags(schema),
    }));
  }

  private getSchemaTags(schema: any): string[] {
    if (!schema || typeof schema !== 'object') {
      return [];
    }

    const tags: string[] = [];
    const rawType = typeof schema.type === 'string' ? schema.type : '';
    if (rawType === 'array') {
      const itemType = typeof schema.items?.type === 'string' ? schema.items.type : '';
      tags.push(itemType ? `Liste<${this.translateSchemaType(itemType)}>` : 'Liste');
    } else if (rawType) {
      tags.push(this.translateSchemaType(rawType));
    }

    if (typeof schema.entity === 'string' && schema.entity.trim()) {
      tags.push(schema.entity.trim());
    }

    return [...new Set(tags)];
  }

  private translateSchemaType(type: string): string {
    switch (type.toLowerCase()) {
      case 'string':
        return 'Text';
      case 'number':
      case 'integer':
        return 'Zahl';
      case 'boolean':
        return 'Boolean';
      case 'object':
        return 'Objekt';
      case 'array':
        return 'Liste';
      default:
        return type;
    }
  }

  private toDisplayLabel(value: string): string {
    const withSpaces = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!withSpaces) return 'Parameter';
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }

  private getStepDisplayName(step: any): string {
    const value = step?.inputs?.['__name'] ?? step?.name ?? step?.moduleId ?? '';
    return String(value).trim();
  }

  private loadDocumentClasses(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) {
      this.availableDocumentClasses = [];
      return;
    }

    this.documentClassService.list(projectId).subscribe({
      next: response => {
        this.availableDocumentClasses = (response.classes ?? [])
          .map(documentClass => ({id: documentClass.id, name: documentClass.title}));
        // Die Klassen treffen unabhängig vom Regelset ein: kommen sie später, steht im Formular
        // noch die leere Auswahl – sie wird hier nachgezogen. Der Nutzer hat dabei nichts geändert,
        // deshalb darf der Editor davon nicht „ungespeichert" werden.
        const wasPristine = !this.isDirty;
        if (this.backfillAllowedDocumentClassesIfEmpty() && wasPristine) {
          this.markSaved();
        }
      },
      error: () => {
        this.availableDocumentClasses = [];
      },
    });
  }

  /**
   * Solange keine Einschränkung getroffen wurde, gelten alle Dokumentenklassen als erlaubt.
   *
   * @returns true, wenn die Auswahl dadurch gesetzt wurde
   */
  private backfillAllowedDocumentClassesIfEmpty(): boolean {
    if (this.getAllowedDocumentClassesValue().length > 0) return false;
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    if (allDocumentClassIds.length === 0) return false;
    this.form.get('allowedDocumentClasses')?.setValue(allDocumentClassIds);
    return true;
  }

  private parseStoredDocumentClasses(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is string => typeof v === 'string');
  }

  private parseStoredRuleAllowedDocumentClasses(value: unknown): Record<string, string[]> {
    if (!value || typeof value !== 'object') return {};
    const entries = Object.entries(value as Record<string, unknown>).map(([ruleId, selectedDocumentClasses]) => [
      ruleId,
      this.parseStoredDocumentClasses(selectedDocumentClasses),
    ]);
    return Object.fromEntries(entries);
  }

  /** Parse per-rule Regelparameter (tag) values from config: { ruleId: { tagId: value } }. */
  private parseStoredRuleTagValues(value: unknown): Record<string, Record<string, string>> {
    if (!value || typeof value !== 'object') return {};
    const entries = Object.entries(value as Record<string, unknown>).map(([ruleId, tagMap]) => {
      const clean: Record<string, string> = {};
      if (tagMap && typeof tagMap === 'object') {
        for (const [tagId, val] of Object.entries(tagMap as Record<string, unknown>)) {
          if (typeof val === 'string' && val) clean[tagId] = val;
        }
      }
      return [ruleId, clean];
    });
    return Object.fromEntries(entries);
  }

  private parseStoredAllowedFileTypes(value: unknown): string[] {
    const mapped = this.parseStoredRuleAllowedFileTypeSelection(value);

    return mapped.length ? mapped : [...this.availableFileTypes];
  }

  private parseStoredRuleAllowedFileTypeSelection(value: unknown): string[] {
    const normalizedValues = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',').map(v => v.trim()).filter(Boolean)
        : [];

    return normalizedValues
      .map(v => String(v).toLowerCase())
      .map(v => {
        if (v === '.ifc' || v === 'ifc') return 'IFC';
        if (v === '.pdf' || v === 'pdf') return 'PDF';
        if (v === '.xlsx' || v === 'excel') return 'Excel';
        if (v === '.docx' || v === 'word') return 'Word';
        if (v === '.pptx' || v === 'powerpoint') return 'PowerPoint';
        return '';
      })
      .filter(Boolean);
  }

  private parseStoredRuleAllowedFileTypes(value: unknown): Record<string, string[]> {
    if (!value || typeof value !== 'object') return {};
    const entries = Object.entries(value as Record<string, unknown>).map(([ruleId, selectedFileTypes]) => [
      ruleId,
      this.parseStoredRuleAllowedFileTypeSelection(selectedFileTypes),
    ]);
    return Object.fromEntries(entries);
  }

  private serializeAllowedFileTypes(value: unknown): string[] {
    const selected = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === 'string')
      : [];

    return selected
      .map(v => {
        if (v === 'IFC') return '.ifc';
        if (v === 'PDF') return '.pdf';
        if (v === 'Excel') return '.xlsx';
        if (v === 'Word') return '.docx';
        if (v === 'PowerPoint') return '.pptx';
        return '';
      })
      .filter(Boolean);
  }

  onDeleteRule(rule: HybridRule): void {
    this.ruleToDelete = rule;
    // Verwendung in myChecks nachladen: eine Hybrid-Regel kann dort einzeln referenziert sein.
    this.deleteUsages = [];
    this.deleteUsagesLoading = true;
    this.rulesetService.findMyCheckUsages([rule.id]).subscribe({
      next: usages => {
        if (this.ruleToDelete?.id !== rule.id) return;
        this.deleteUsages = usages;
        this.deleteUsagesLoading = false;
      },
      error: () => {
        if (this.ruleToDelete?.id !== rule.id) return;
        this.deleteUsagesLoading = false;
      },
    });
  }

  cancelDelete(): void {
    this.ruleToDelete = null;
    this.deleteUsages = [];
    this.deleteUsagesLoading = false;
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: [this.ruleset.name],
      description: [this.ruleset.description],
      allowedDocumentClasses: [[]],
      allowedFileTypes: [this.ruleset.allowedFileTypes],
      checkMode: [this.ruleset.checkMode],
    });
  }

  protected readonly faChevronRight = faChevronRight;

  private restoreSetConfig(config: any): void {
    const parsedAllowedDocumentClasses = this.parseStoredDocumentClasses(config.allowedDocumentClasses);
    const allowedDocumentClasses = parsedAllowedDocumentClasses.length
      ? parsedAllowedDocumentClasses
      : this.availableDocumentClasses.map(documentClass => documentClass.id);
    const allowedFileTypes = this.parseStoredAllowedFileTypes(config.allowedFileTypes);
    this.form.patchValue({
      allowedDocumentClasses,
      allowedFileTypes: allowedFileTypes.length ? allowedFileTypes : this.ruleset.allowedFileTypes,
      checkMode: config.checkMode ?? this.ruleset.checkMode,
    }, {emitEvent: false});

    this.inputParameters = config.inputParameters?.length ? config.inputParameters : [
      {id: crypto.randomUUID(), name: 'Dateitext', dataType: 'Text', necessity: 'Pflicht'},
      {id: crypto.randomUUID(), name: 'Dateiname', dataType: 'Text', necessity: 'Pflicht'},
    ];
    this.checkParameters = config.checkParameters?.length ? config.checkParameters : [
      {id: crypto.randomUUID(), name: 'Identifikation', dataType: 'Text', necessity: 'Pflicht'},
      {id: crypto.randomUUID(), name: 'Abgrenzung', dataType: 'Text', necessity: 'Pflicht'},
      {id: crypto.randomUUID(), name: 'Anwendungsbereich', dataType: 'Text', necessity: 'Pflicht'},
      {id: crypto.randomUUID(), name: 'Inhalt', dataType: 'Text', necessity: 'Pflicht'},
    ];
    this.tags = Array.isArray(config.tags) ? config.tags : [];
    this.eingabeparameter = this.restoreEingabeparameter(config);
    this.pruefebene = normalizePruefebene(config.pruefebene);
    if (config.kiModel) this.aiModel = config.kiModel;
    const storedIdentifikator = typeof config.identifikatorParamId === 'string' ? config.identifikatorParamId : '';
    this.identifikatorParamId = this.eingabeparameter.some(param => param.id === storedIdentifikator)
      ? storedIdentifikator
      : (this.eingabeparameter[0]?.id ?? '');
    this.pruefebeneFilter = this.pruefebene !== 'gesamtdokument' && typeof config.pruefebeneFilter === 'string'
      ? config.pruefebeneFilter
      : '';
    const savedOutputParams = config.outputParameters?.length ? config.outputParameters : null;
    const filteredOutputParams = savedOutputParams
      ? savedOutputParams.filter((p: any) =>
        p.modus !== 'Prüfparameter' &&
        !(p.modus === 'KI-Generiert' && p.name === 'Regeltyp'))
      : null;
    this.outputParameters = filteredOutputParams?.length ? filteredOutputParams : [
      {
        modus: 'KI-Generiert',
        name: 'Ergebnis',
        wert: 'Gesamtergebnis der Prüfung entweder SUCCESS, FAILURE, WARNING oder UNKNOWN (falls die Datei für die Regel nicht relevant ist oder keine ausreichende Information für ein Ergebnis vorliegt)',
        dateityp: 'Text'
      },
      {
        modus: 'KI-Generiert',
        name: 'Bewertung',
        wert: 'Bewertung der Vollständigkeit zwischen 0 und 1',
        dateityp: 'Zahl'
      },
      {
        modus: 'KI-Generiert',
        name: 'Antwort',
        wert: 'Kurze sachliche Antwort, warum die Regel erfüllt wurde oder nicht.',
        dateityp: 'Text'
      },
    ];
    // Altformat überführen: Auswahl-Parameter trugen ihre Optionen kommasepariert im `wert`-Feld.
    this.outputParameters = migrateOutputParameterOptions(this.outputParameters);
  }
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faListCheck = faListCheck;

  private reconcileRuleAllowedSelections(): void {
    const allowedDocumentClasses = new Set(this.getEffectiveAllowedDocumentClasses());
    const allowedFileTypes = new Set(this.getEffectiveAllowedFileTypes());
    this.rules = this.rules.map(rule => ({
      ...rule,
      // seed:<KEY>-Tokens (Standardklassen in global geteilten Kopien) immer durchreichen: im
      // projektlosen Admin-Editor ist availableDocumentClasses leer, und ein Herausfiltern würde
      // die Einschränkung beim nächsten Speichern der globalen Kopie still entfernen.
      allowedDocumentClasses: (rule.allowedDocumentClasses ?? []).filter(documentClass =>
        documentClass.startsWith('seed:') || allowedDocumentClasses.has(documentClass)),
      allowedFileTypes: (rule.allowedFileTypes ?? []).filter(fileType => allowedFileTypes.has(fileType)),
    }));
  }
}
